import { LOOK, isDebugOn } from "./dev";
import { multiply, perspective as perspectiveZeroToOne } from "../roulette/mat4";
import { Backend, Blend, DynamicMesh, FLOATS_PER_VERTEX, Frame, MAX_DRAWS, Mesh, PACKED_STRIDE, Paint } from "./backend";
import { createGlBackend } from "./backend-gl";
import { createGpuBackend } from "./backend-gpu";
import { HEMISPHERE } from "./palette";
import { BODIES, FaceState, KartKind, ModelData, Pose, ROSTER, characterOf, loadModel } from "./models";
import {
  BARF_TIME, BASS_BEAT, BASS_RADIUS, DUCK_GONE, JOLT_TIME, Race, SMOKE_RADIUS, SNOWMAN_GONE, STEP, COUNTDOWN, driftTier, duckAt, isPuddle, isPuddleItem, raceTime,
} from "./sim";
import {
  BALL_RADIUS, BRANCH_WIDTH, CROSSING, CROSSING_SPAN, CUT_GATE, CUT_HALF, GATE_LENGTH, PAD_LENGTH, PAD_WIDTH, POOL_RADIUS,
  POST_RADIUS, RAMP_HEIGHT, RAMP_LENGTH, RAMP_WIDTH, Rgb, SWARM_RADIUS, TIMBER_LENGTH, TIMBER_WIDTH, Theme, Track,
  crossingPhase, cutHalf, hazardLat, inWater, lakeRadius, locate, loopDelta, nearestCut, roadY,
} from "./tracks";
import { TIER_COLORS, TICK_STEP, tickSamples, tierSpans } from "./trackDebug";
import { TUNING } from "./tuning";

// Everything about drawing a race that isn't a graphics API: the geometry,
// the camera, who stands where and in what colours, fire and sprites. Each
// frame it fills a list of draw commands — from pooled objects and
// preallocated buffers, so a frame allocates nothing for the collector to
// stall on — and hands it to a backend: WebGPU where the device has it
// (backend-gpu.ts), WebGL where it doesn't (backend-gl.ts). The look is the
// toy-box one of the phone kart games; karts and drivers are the Blender
// models (models.ts), painted per member, with a kart built from boxes here
// standing in until they've loaded.

/** How often a ghost's line is recorded (seconds of race). */
export const GHOST_STEP = 0.1;

export interface KartRenderer {
  readonly kind: "webgpu" | "webgl";
  setRace(race: Race): void;
  /** A ghost to race: a recorded run's line, GHOST_STEP apart — x, y, z, heading each — or null for none. */
  setGhost(path: number[] | null): void;
  resize(width: number, height: number, dpr: number): void;
  /** Shake the camera: a hit, a landing. */
  kick(amount: number): void;
  /** `alpha`: how far we are from the last sim step towards the next, 0…1. */
  render(race: Race, alpha: number, dt: number): void;
  /** The last frame: draw calls, triangles, and its camera — for the debug readout and scripts/kart-shots. */
  readonly stats: { draws: number; triangles: number; viewProj: Float32Array; particles: number; overdraw: number };
  destroy(): void;
}

// ── Geometry ─────────────────────────────────────────────────────────────────

/** Geometry as flat arrays: position, normal, colour, uv — then indices. */
class MeshBuilder {
  v: number[] = [];
  i: number[] = [];

  vert(p: number[], n: number[], c: Rgb, uv: number[] = [0, 0]) {
    this.v.push(p[0], p[1], p[2], n[0], n[1], n[2], c[0], c[1], c[2], uv[0], uv[1]);
    return this.v.length / FLOATS_PER_VERTEX - 1;
  }

  quad(a: number[], b: number[], c: number[], d: number[], n: number[], col: Rgb, uvs?: number[][]) {
    const k = this.vert(a, n, col, uvs?.[0]);
    this.vert(b, n, col, uvs?.[1]);
    this.vert(c, n, col, uvs?.[2]);
    this.vert(d, n, col, uvs?.[3]);
    this.i.push(k, k + 1, k + 2, k, k + 2, k + 3);
  }

  box(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, col: Rgb) {
    const [x0, x1, y0, y1, z0, z1] = [cx - sx / 2, cx + sx / 2, cy - sy / 2, cy + sy / 2, cz - sz / 2, cz + sz / 2];
    const shade = (f: number): Rgb => [col[0] * f, col[1] * f, col[2] * f];
    // The sides darken towards the foot, as if lit from the sky: ambient occlusion, baked.
    const foot = shade(0.8);
    const side = (a: number[], b: number[], c: number[], d: number[], n: number[]) => {
      const k = this.vert(a, n, foot);
      this.vert(b, n, col);
      this.vert(c, n, col);
      this.vert(d, n, foot);
      this.i.push(k, k + 1, k + 2, k, k + 2, k + 3);
    };
    this.quad([x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1], [0, 1, 0], col);
    this.quad([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], [0, -1, 0], shade(0.7));
    side([x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1], [1, 0, 0]);
    side([x0, y0, z0], [x0, y1, z0], [x0, y1, z1], [x0, y0, z1], [-1, 0, 0]);
    side([x0, y0, z1], [x0, y1, z1], [x1, y1, z1], [x1, y0, z1], [0, 0, 1]);
    side([x0, y0, z0], [x0, y1, z0], [x1, y1, z0], [x1, y0, z0], [0, 0, -1]);
  }

  /** A cylinder along z (a wheel, sideways) or y (a trunk). */
  cylinder(cx: number, cy: number, cz: number, r: number, len: number, col: Rgb, axis: "y" | "z", sides = 10) {
    for (let s = 0; s < sides; s++) {
      const a0 = (s / sides) * Math.PI * 2;
      const a1 = ((s + 1) / sides) * Math.PI * 2;
      const [c0, s0, c1, s1] = [Math.cos(a0), Math.sin(a0), Math.cos(a1), Math.sin(a1)];
      const P = (c: number, s: number, h: number) =>
        axis === "z" ? [cx + c * r, cy + s * r, cz + h] : [cx + c * r, cy + h, cz + s * r];
      const N = (c: number, s: number) => (axis === "z" ? [c, s, 0] : [c, 0, s]);
      const cap = axis === "z" ? [0, 0, 1] : [0, 1, 0];
      const h = len / 2;
      this.quad(P(c0, s0, -h), P(c1, s1, -h), P(c1, s1, h), P(c0, s0, h), N((c0 + c1) / 2, (s0 + s1) / 2), col);
      const centre = axis === "z" ? [cx, cy, cz + h] : [cx, cy + h, cz];
      this.quad(centre, P(c0, s0, h), P(c1, s1, h), centre, cap, col);
      const under = axis === "z" ? [cx, cy, cz - h] : [cx, cy - h, cz];
      this.quad(under, P(c0, s0, -h), P(c1, s1, -h), under, cap.map((v) => -v), col);
    }
  }

  /** A ball, in bands of latitude — one colour, or one per facet. */
  sphere(cx: number, cy: number, cz: number, r: number, col: Rgb | ((ring: number, side: number) => Rgb), rings = 6, sides = 10) {
    const P = (ring: number, side: number) => {
      const a = (ring / rings) * Math.PI;
      const b = (side / sides) * Math.PI * 2;
      return [Math.sin(a) * Math.cos(b), -Math.cos(a), Math.sin(a) * Math.sin(b)];
    };
    for (let ring = 0; ring < rings; ring++) {
      for (let side = 0; side < sides; side++) {
        const corners = [P(ring, side), P(ring, side + 1), P(ring + 1, side + 1), P(ring + 1, side)];
        const k = this.count;
        const facet = typeof col === "function" ? col(ring, side) : col;
        for (const c of corners) this.vert([cx + c[0] * r, cy + c[1] * r, cz + c[2] * r], c, facet);
        this.i.push(k, k + 1, k + 2, k, k + 2, k + 3);
      }
    }
  }

  /** A log lying level from (ax, az) to (bx, bz), its axis `y` up; `ends` colours its cut faces. */
  log(ax: number, az: number, bx: number, bz: number, r: number, col: Rgb, ends = col, y = r, sides = 6) {
    const len = Math.hypot(bx - ax, bz - az) || 1;
    const [dx, dz] = [(bx - ax) / len, (bz - az) / len];
    const P = (end: number, a: number) => {
      const [c, s] = [Math.cos(a) * r, Math.sin(a) * r];
      return [(end ? bx : ax) - dz * c, y + s, (end ? bz : az) + dx * c];
    };
    for (let k = 0; k < sides; k++) {
      const [a0, a1] = [(k / sides) * Math.PI * 2, ((k + 1) / sides) * Math.PI * 2];
      const mid = (a0 + a1) / 2;
      this.quad(P(0, a0), P(0, a1), P(1, a1), P(1, a0), [-dz * Math.cos(mid), Math.sin(mid), dx * Math.cos(mid)], col);
      this.quad([ax, y, az], P(0, a0), P(0, a1), [ax, y, az], [-dx, 0, -dz], ends);
      this.quad([bx, y, bz], P(1, a0), P(1, a1), [bx, y, bz], [dx, 0, dz], ends);
    }
  }

  /** A flat, roughly round patch at height y — a puddle, spilt slush — its edge wobbling by `wobble`. */
  blob(cx: number, cz: number, r: number, y: number, col: Rgb, wobble = 0.1, sides = 20, seed = 0) {
    const edge = (k: number) => {
      const a = (k / sides) * Math.PI * 2;
      const rr = r * (1 + wobble * Math.sin(a * 3 + seed) + wobble * 0.6 * Math.sin(a * 5 + seed * 2.3));
      return [cx + Math.cos(a) * rr, y, cz + Math.sin(a) * rr];
    };
    for (let k = 0; k < sides; k++) this.quad([cx, y, cz], edge(k), edge(k + 1), [cx, y, cz], [0, 1, 0], col);
  }

  /** Turn everything built since vertex `from` about y by `yaw`, then move it to (x, y, z). */
  moveSince(from: number, x: number, y: number, z: number, yaw = 0) {
    const [c, s] = [Math.cos(yaw), Math.sin(yaw)];
    for (let o = from * FLOATS_PER_VERTEX; o < this.v.length; o += FLOATS_PER_VERTEX) {
      const v = this.v;
      for (const k of [0, 3]) {
        const [px, pz] = [v[o + k], v[o + k + 2]];
        v[o + k] = px * c - pz * s;
        v[o + k + 2] = px * s + pz * c;
      }
      v[o] += x;
      v[o + 1] += y;
      v[o + 2] += z;
    }
  }

  get count() {
    return this.v.length / FLOATS_PER_VERTEX;
  }

  cone(cx: number, cy: number, cz: number, r: number, h: number, col: Rgb, sides = 8) {
    for (let s = 0; s < sides; s++) {
      const a0 = (s / sides) * Math.PI * 2;
      const a1 = ((s + 1) / sides) * Math.PI * 2;
      const mid = (a0 + a1) / 2;
      const tip = [cx, cy + h, cz];
      const p0 = [cx + Math.cos(a0) * r, cy, cz + Math.sin(a0) * r];
      const p1 = [cx + Math.cos(a1) * r, cy, cz + Math.sin(a1) * r];
      const len = Math.hypot(h, r);
      // Darker at the foot, lighter at the tip.
      const n = [(Math.cos(mid) * h) / len, r / len, (Math.sin(mid) * h) / len];
      const k = this.vert(p0, n, [col[0] * 0.78, col[1] * 0.78, col[2] * 0.78]);
      this.vert(p1, n, [col[0] * 0.78, col[1] * 0.78, col[2] * 0.78]);
      this.vert(tip, n, [Math.min(1, col[0] * 1.2), Math.min(1, col[1] * 1.2), Math.min(1, col[2] * 1.2)]);
      this.i.push(k, k + 1, k + 2);
    }
  }
}

const KART_COLORS: Rgb[] = [
  [0.93, 0.2, 0.18], [0.16, 0.45, 0.93], [0.15, 0.72, 0.3], [0.98, 0.78, 0.1],
  [0.62, 0.3, 0.9], [0.98, 0.5, 0.1], [0.1, 0.75, 0.8], [0.95, 0.4, 0.7],
  [0.25, 0.25, 0.3], [0.96, 0.96, 0.96],
];

/** Karts for anyone outside the roster, picked from their id. */
const OTHER_KARTS: KartKind[] = ["classic", "racer", "buggy", "brute"];

/**
 * A racer's kart: their character's own — its kind and colours, from the
 * roster (Nichlas's is his boat) — and, for the stand-in kart drawn while
 * the models load, wheels and trimmings picked from their id.
 */
export function kartStyle(r: { id: number; name: string; char?: string }) {
  const char = characterOf(r);
  const c = char && ROSTER[char];
  let h = Math.imul(r.id + 1, 2654435761) >>> 0;
  const pick = (m: number) => {
    const v = h % m;
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
    return v;
  };
  const body = pick(KART_COLORS.length);
  const accent = (body + 1 + pick(KART_COLORS.length - 1)) % KART_COLORS.length;
  const kind = OTHER_KARTS[pick(OTHER_KARTS.length)];
  return {
    char,
    kind: c ? (c.kart as KartKind) : kind,
    body: c ? (c.paint[0] as Rgb) : KART_COLORS[body],
    accent: c ? (c.paint[1] as Rgb) : KART_COLORS[accent],
    wheel: 0.36 + pick(3) * 0.07,
    spoiler: pick(2) === 1,
    pipes: 1 + pick(2),
    long: pick(2) === 1,
  };
}

/**
 * Every kart's style for a race, no two the same colour: the roster's are
 * all different already, and anyone else keeps their own unless someone
 * before them has it — the player goes first, so theirs is always their own.
 */
export function fieldStyles(karts: { id: number; name: string; char?: string; player?: boolean }[]) {
  const taken = new Set<string>();
  const styles = new Map<number, ReturnType<typeof kartStyle>>();
  for (const k of [...karts].sort((a, b) => Number(!!b.player) - Number(!!a.player))) {
    const style = kartStyle(k);
    if (taken.has(String(style.body))) style.body = KART_COLORS.find((c) => !taken.has(String(c)) && c !== style.accent) ?? style.body;
    taken.add(String(style.body));
    styles.set(k.id, style);
  }
  return styles;
}

function buildKart(r: { id: number; name: string; char?: string }) {
  const st = kartStyle(r);
  const b = new MeshBuilder();
  const len = st.long ? 2.7 : 2.3;
  const dark: Rgb = [0.13, 0.13, 0.15];
  const metal: Rgb = [0.7, 0.72, 0.76];
  // Built facing +x, the right-hand side along +z.
  b.box(0, 0.5, 0, len, 0.4, 1.3, st.body);
  b.box(len / 2 + 0.15, 0.45, 0, 0.5, 0.3, 1.0, st.accent);
  b.box(len / 2 + 0.45, 0.35, 0, 0.2, 0.2, 1.6, dark);
  b.box(-0.35, 0.9, 0, 0.35, 0.7, 1.0, st.accent);
  b.box(-len / 2 + 0.1, 0.75, 0, 0.6, 0.5, 1.1, dark);
  for (let p = 0; p < st.pipes; p++) {
    const z = st.pipes === 1 ? 0 : p ? 0.3 : -0.3;
    b.cylinder(-len / 2 - 0.2, 0.75, z, 0.13, 0.5, metal, "y", 8);
  }
  if (st.spoiler) {
    b.box(-len / 2 - 0.1, 1.35, 0, 0.35, 0.08, 1.7, st.body);
    b.box(-len / 2, 1.1, 0.55, 0.1, 0.45, 0.1, dark);
    b.box(-len / 2, 1.1, -0.55, 0.1, 0.45, 0.1, dark);
  }
  for (const [x, z] of [[len / 2 - 0.35, 0.85], [len / 2 - 0.35, -0.85], [-len / 2 + 0.45, 0.9], [-len / 2 + 0.45, -0.9]]) {
    const r = x < 0 ? st.wheel + 0.06 : st.wheel;
    b.cylinder(x, r, z, r, 0.4, dark, "z");
    b.cylinder(x, r, z + Math.sign(z) * 0.21, r * 0.45, 0.04, st.accent, "z", 8);
  }
  return b;
}

/**
 * How high the snow is at a point, where the road climbs: as high as the road
 * right beside it; further off, a smooth mountainside through all the road's
 * heights, rising into peaks the further it gets from any road. Flat maps: 0.
 */
export function terrainY(track: Track, x: number, z: number) {
  const { samples, half } = track;
  if (!samples.some((s) => s.y > 0)) return 0;
  let best = Infinity;
  let y = 0;
  let sum = 0;
  let weight = 0;
  for (const s of samples) {
    const d = (s.x - x) ** 2 + (s.z - z) ** 2;
    if (d < best) {
      best = d;
      y = s.y;
    }
    const w = 1 / (d + 100) ** 2;
    sum += s.y * w;
    weight += w;
  }
  const dist = Math.sqrt(best);
  const peaks = Math.min(70, Math.max(0, dist - 35) * 0.9) * (0.75 + 0.25 * Math.sin(x * 0.045) * Math.cos(z * 0.06));
  const t = Math.min(1, Math.max(0, (dist - half - 4.4) / 25));
  return y + (sum / weight + peaks - y) * t * t * (3 - 2 * t) - 0.4;
}

export function buildWorld(track: Track, debug = false) {
  const { samples, n, half, map } = track;
  const th = map.theme;
  const world = new MeshBuilder();
  const road = new MeshBuilder();
  const pads = new MeshBuilder();
  const props = new MeshBuilder();
  // A mesh's indices are 16-bit: the forest needs more than one mesh of its own.
  const forest = [new MeshBuilder()];
  const prop = () => {
    if (forest[forest.length - 1].count > 60000) forest.push(new MeshBuilder());
    return forest[forest.length - 1];
  };
  /** On a dirt road, the chequered start line alone: the road is drawn plain. */
  const line = new MeshBuilder();
  /** Lakes' beds and shores, reeds, hedges, flowers — on a mesh of its own, to stay in 16-bit indices. */
  const details = new MeshBuilder();
  /** The lakes' surface, drawn see-through over their beds (and the flooded road). */
  const water = new MeshBuilder();
  /** Snowy hillsides, where the road climbs — its own mesh, for the same reason as details. */
  const terrain = new MeshBuilder();
  /** Lettering, from the sprite atlas: the sign over the chalet door. */
  const sign = new MeshBuilder();
  const inCut = (x: number, z: number, r = 0) => {
    const near = nearestCut(track, x, z);
    return !!near && near.dist < near.half + r;
  };

  const G = 900;
  world.quad([-G, -0.05, -G], [G, -0.05, -G], [G, -0.05, G], [-G, -0.05, G], [0, 1, 0], th.ground);

  const at = (i: number, lat: number, y: number) => {
    const s = samples[i % n];
    return [s.x + s.nx * lat, s.y + y, s.z + s.nz * lat];
  };
  const red: Rgb = [0.88, 0.18, 0.15];
  const white: Rgb = [0.97, 0.97, 0.97];
  const wallH = 2.2;
  const wallW = 1.4;
  /** A fixed 0…1 for each whole number, to vary stones and dirt by. */
  const hash = (k: number) => {
    const v = Math.sin(k * 12.9898) * 43758.5453;
    return v - Math.floor(v);
  };
  const tint = (c: Rgb, f: number): Rgb => [Math.min(1, c[0] * f), Math.min(1, c[1] * f), Math.min(1, c[2] * f)];
  const hilly = samples.some((s) => s.y > 0);
  /** A strip along the road from sample i to the next, `l0` to `l1` across, shading from `c0` to `c1`. */
  const band = (b: MeshBuilder, i: number, l0: number, l1: number, y: number, c0: Rgb, c1: Rgb) => {
    const k = b.vert(at(i, l0, y), [0, 1, 0], c0);
    b.vert(at(i, l1, y), [0, 1, 0], c1);
    b.vert(at(i + 1, l1, y), [0, 1, 0], c1);
    b.vert(at(i + 1, l0, y), [0, 1, 0], c0);
    b.i.push(k, k + 1, k + 2, k, k + 2, k + 3);
  };
  for (let i = 0; i < n; i++) {
    const j = i + 1;
    const s = samples[i];
    // The road's width here, which the walls, kerbs and stones follow.
    const half = s.half;
    const uv = [[0, i], [1, i], [1, j], [0, j]];
    if (th.dirt) {
      // Packed earth, a shade lighter or darker a stretch at a time, with two
      // worn ruts along it.
      road.quad(at(i, -half, 0), at(i, half, 0), at(j, half, 0), at(j, -half, 0), [0, 1, 0], tint(th.road, 0.96 + 0.08 * hash(i >> 2)), uv);
      for (const rut of [-2.6, 2.6]) {
        road.quad(at(i, rut - 0.55, 0.01), at(i, rut + 0.55, 0.01), at(j, rut + 0.55, 0.01), at(j, rut - 0.55, 0.01), [0, 1, 0], tint(th.road, th.ice ? 0.94 : 0.84));
      }
      if (i < 3) line.quad(at(i, -half, 0.015), at(i, half, 0.015), at(j, half, 0.015), at(j, -half, 0.015), [0, 1, 0], th.road, uv);
      // A paler band along each edge, worn into the dirt; on the flat, the
      // grass beyond the stones fading in from the road's colour.
      for (const side of [-1, 1]) {
        band(road, i, side * (half - 1.1), side * half, 0.008, th.road, tint(th.road, 1.14));
        if (!hilly) band(road, i, side * (half + wallW), side * (half + wallW + 2.6), -0.03, mixRgb(th.road, th.ground, 0.45), th.ground);
      }
    } else {
      road.quad(at(i, -half, 0), at(i, half, 0), at(j, half, 0), at(j, -half, 0), [0, 1, 0], th.road, uv);
    }
    const stripe = Math.floor(i / 2) % 2 ? red : white;
    for (const side of [-1, 1]) {
      const inner = side * half;
      const outer = side * (half + wallW);
      // A gap in the wall where a shortcut leaves the road.
      const [gx, , gz] = at(i, inner, 0);
      if (inCut(gx, gz)) continue;
      if (th.logs) {
        // Low logs along the edges, one every few units, not quite the same size.
        if (i % 5) continue;
        const r = 0.4 + ((i / 5) % 3) * 0.05;
        const [ax, , az] = at(i, side * (half + r), 0);
        const [bx, , bz] = at(i + 5, side * (half + r), 0);
        prop().log(ax, az, bx, bz, r, th.wall, th.wallTop);
        continue;
      }
      if (th.ice) {
        // Blocks of ice, big and small, each a little askew — every other
        // unit a new one, some with a smaller one stacked on top.
        if (i % 2) continue;
        const k = i * 2 + side;
        const len = 1.3 + 1.4 * hash(k);
        const h = 0.6 + 1.3 * hash(k + 0.3);
        const w = 1 + 0.8 * hash(k + 0.6);
        const f = 0.9 + 0.15 * hash(k + 0.9);
        const yaw = Math.atan2(s.tz, s.tx) + (hash(k + 0.1) - 0.5) * 0.5;
        const [x, y, z] = at(i, side * (half + w / 2 + 0.1), 0);
        const from = world.count;
        world.box(0, (h - 0.5) / 2, 0, len, h + 0.5, w, tint(th.wall, f));
        if (hash(k + 0.2) < 0.3) {
          const top = 0.4 + 0.5 * hash(k + 0.4);
          world.box((hash(k + 0.7) - 0.5) * 0.5, h + top / 2, 0, len * 0.6, top, w * 0.7, tint(th.wallTop, f));
        }
        world.moveSince(from, x, y, z, yaw);
        continue;
      }
      if (th.dirt) {
        // A row of low stones, two units long each, every one a little
        // different in height and shade, rounded off along the top.
        const k = (i >> 1) * 2 + side;
        const h = 0.55 + 0.4 * hash(k);
        const f = 0.9 + 0.2 * hash(k + 0.5);
        const [wall, top] = [tint(th.wall, f), tint(th.wallTop, f)];
        const [a, b] = [inner + side * 0.25, outer - side * 0.25];
        const [nx, nz] = [side * s.nx * 0.8, side * s.nz * 0.8];
        world.quad(at(i, inner, 0), at(i, a, h), at(j, a, h), at(j, inner, 0), [-nx, 0.6, -nz], wall);
        world.quad(at(i, a, h), at(i, b, h), at(j, b, h), at(j, a, h), [0, 1, 0], top);
        world.quad(at(i, outer, 0), at(i, b, h), at(j, b, h), at(j, outer, 0), [nx, 0.6, nz], wall);
        // Ends, where one stone meets the next.
        const end = i % 2 ? j : i;
        const t = [(i % 2 ? 1 : -1) * s.tx, 0, (i % 2 ? 1 : -1) * s.tz];
        world.quad(at(end, inner, 0), at(end, a, h), at(end, b, h), at(end, outer, 0), t, tint(wall, 0.9));
        continue;
      }
      // Kerb along the foot of the wall.
      world.quad(at(i, inner - side * 0.8, 0.02), at(i, inner, 0.02), at(j, inner, 0.02), at(j, inner - side * 0.8, 0.02), [0, 1, 0], stripe);
      world.quad(at(i, inner, 0), at(i, inner, wallH), at(j, inner, wallH), at(j, inner, 0), [-side * s.nx, 0, -side * s.nz], th.wall);
      world.quad(at(i, inner, wallH), at(i, outer, wallH), at(j, outer, wallH), at(j, inner, wallH), [0, 1, 0], th.wallTop);
      world.quad(at(i, outer, 0), at(i, outer, wallH), at(j, outer, wallH), at(j, outer, 0), [side * s.nx, 0, side * s.nz], th.wall);
    }
  }

  for (const pad of track.pads) {
    const i0 = pad.idx - Math.floor(PAD_LENGTH / 2);
    for (let k = 0; k < PAD_LENGTH; k++) {
      const i = (i0 + k + n) % n;
      const l0 = pad.lat - PAD_WIDTH / 2;
      const l1 = pad.lat + PAD_WIDTH / 2;
      pads.quad(at(i, l0, 0.04), at(i, l1, 0.04), at(i + 1, l1, 0.04), at(i + 1, l0, 0.04), [0, 1, 0], white, [
        [0, k / PAD_LENGTH], [1, k / PAD_LENGTH], [1, (k + 1) / PAD_LENGTH], [0, (k + 1) / PAD_LENGTH],
      ]);
    }
  }

  const up = [0, 1, 0];

  // Where the road climbs, the snow climbs with it, and mountains round it all.
  const groundY = (x: number, z: number) => (hilly ? terrainY(track, x, z) : 0);
  if (hilly) {
    const [cell, margin] = [5, 110];
    const xs = samples.map((s) => s.x);
    const zs = samples.map((s) => s.z);
    const [x0, z0] = [Math.min(...xs) - margin, Math.min(...zs) - margin];
    const cols = Math.ceil((Math.max(...xs) + margin - x0) / cell) + 1;
    const rows = Math.ceil((Math.max(...zs) + margin - z0) / cell) + 1;
    const hs = new Float32Array(cols * rows);
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) hs[r * cols + c] = groundY(x0 + c * cell, z0 + r * cell);
    const h = (r: number, c: number) => hs[Math.min(rows - 1, Math.max(0, r)) * cols + Math.min(cols - 1, Math.max(0, c))];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const [nx, ny, nz] = [h(r, c - 1) - h(r, c + 1), 2 * cell, h(r - 1, c) - h(r + 1, c)];
        const l = Math.hypot(nx, ny, nz);
        // Too steep for snow to lie on: bare grey rock.
        const ground = ny / l < 0.7 ? [0.55, 0.56, 0.6] as Rgb : th.ground;
        terrain.vert([x0 + c * cell, h(r, c), z0 + r * cell], [nx / l, ny / l, nz / l], tint(ground, 0.97 + 0.03 * hash(r * cols + c)));
        if (r && c) {
          const k = r * cols + c;
          terrain.i.push(k - cols - 1, k - cols, k, k - cols - 1, k, k - 1);
        }
      }
    }
  }

  if (debug) {
    // For reading a place off the road: each corner tinted by the best drift
    // tier it has room for, and a bar across the road every TICK_STEP of the
    // lap — red at each quarter, longer and where the lap starts.
    for (const { from, len, tier } of tierSpans(track)) {
      for (let k = 0; k < len; k++) {
        const h = samples[(from + k) % n].half;
        details.quad(at(from + k, -h, 0.03), at(from + k, h, 0.03), at(from + k + 1, h, 0.03), at(from + k + 1, -h, 0.03), up, TIER_COLORS[tier]);
      }
    }
    const quarter = Math.round(0.25 / TICK_STEP);
    tickSamples(track).forEach((i, k) => {
      const long = k % quarter === 0 ? 2 : 1;
      const h = samples[i].half;
      details.quad(at(i, -h, 0.05), at(i, h, 0.05), at(i + long, h, 0.05), at(i + long, -h, 0.05), up, k % quarter === 0 ? red : white);
    });
  }

  // Icy stretches: glassy blue, ragged at the edges and tapering in at the ends,
  // with a paler streak or two where the light catches it.
  for (const [from, len] of track.ice) {
    const edge = (k: number, side: number) => side * (samples[(from + k) % n].half - 0.4 - 1.8 * hash((from + k) * 2 + side)) * Math.min(1, Math.min(k, len - k) / 5 + 0.2);
    for (let k = 0; k < len; k++) {
      const [i, j] = [from + k, from + k + 1];
      details.quad(at(i, edge(k, -1), 0.02), at(i, edge(k, 1), 0.02), at(j, edge(k + 1, 1), 0.02), at(j, edge(k + 1, -1), 0.02), up, [0.66, 0.86, 0.98]);
      for (const streak of [-0.35, 0.2]) {
        const [a, b] = [edge(k, 1) * streak, edge(k + 1, 1) * streak];
        details.quad(at(i, a - 0.2, 0.025), at(i, a + 0.2, 0.025), at(j, b + 0.2, 0.025), at(j, b - 0.2, 0.025), up, [0.88, 0.96, 1]);
      }
    }
  }

  // Snow ramps: a white wedge up to the lip, orange along its edges to pick it
  // out. In the forest, planks laid over a log instead, a shade apart each.
  const [rampTop, rampEdge, rampSide]: Rgb[] = th.logs
    ? [[0.72, 0.55, 0.34], [0.5, 0.34, 0.2], [0.42, 0.28, 0.16]]
    : [[0.97, 0.98, 1], [0.98, 0.5, 0.12], [0.8, 0.88, 0.96]];
  for (const ramp of track.ramps) {
    const [l0, l1] = [ramp.lat - RAMP_WIDTH / 2, ramp.lat + RAMP_WIDTH / 2];
    const rise = (k: number) => (RAMP_HEIGHT * k) / RAMP_LENGTH;
    for (let k = 0; k < RAMP_LENGTH; k++) {
      const i = ramp.idx - RAMP_LENGTH + k + n;
      const s = samples[i % n];
      const slope = [-s.tx * 0.26, 0.97, -s.tz * 0.26];
      details.quad(at(i, l0, rise(k)), at(i, l1, rise(k)), at(i + 1, l1, rise(k + 1)), at(i + 1, l0, rise(k + 1)), slope, th.logs ? tint(rampTop, 0.92 + 0.12 * (k % 2)) : rampTop);
      for (const [a, b] of [[l0, l0 + 0.45], [l1 - 0.45, l1]]) {
        details.quad(at(i, a, rise(k) + 0.02), at(i, b, rise(k) + 0.02), at(i + 1, b, rise(k + 1) + 0.02), at(i + 1, a, rise(k + 1) + 0.02), slope, rampEdge);
      }
      for (const [l, side] of [[l0, -1], [l1, 1]]) {
        details.quad(at(i, l, 0), at(i, l, rise(k)), at(i + 1, l, rise(k + 1)), at(i + 1, l, 0), [side * s.nx, 0, side * s.nz], rampSide);
      }
    }
    const s = samples[ramp.idx % n];
    details.quad(at(ramp.idx, l0, 0), at(ramp.idx, l1, 0), at(ramp.idx, l1, RAMP_HEIGHT), at(ramp.idx, l0, RAMP_HEIGHT), [s.tx, 0, s.tz], rampSide);
    if (th.logs) {
      // The log the planks are laid over, under the lip.
      const [ax, , az] = at(ramp.idx, l0 - 0.4, 0);
      const [bx, , bz] = at(ramp.idx, l1 + 0.4, 0);
      details.log(ax - s.tx * 0.6, az - s.tz * 0.6, bx - s.tx * 0.6, bz - s.tz * 0.6, 0.75, [0.46, 0.3, 0.17], [0.8, 0.64, 0.42], 0.75, 8);
    }
  }

  // A log chalet by the road, lit up inside, with its name over the door.
  let house: number[] | null = null;
  if (map.house) {
    const [f, side] = map.house;
    const s = samples[Math.floor(f * n)];
    const [W, D, snow] = [12, 9, [0.96, 0.97, 1] as Rgb];
    const lat = side * (half + wallW + 4 + D / 2);
    house = [s.x + s.nx * lat, s.z + s.nz * lat];
    // Built with its front to -z, the road's way along +x.
    const from = details.count;
    const logs: Rgb[] = [[0.56, 0.36, 0.2], [0.47, 0.29, 0.16]];
    details.box(0, -1.7, 0, W + 0.6, 4, D + 0.6, [0.5, 0.5, 0.55]);
    for (let k = 0; k < 9; k++) {
      const y = 0.55 + k * 0.5;
      details.box(0, y, -D / 2, W + 0.8, 0.48, 0.55, logs[k % 2]);
      details.box(0, y, D / 2, W + 0.8, 0.48, 0.55, logs[k % 2]);
      details.box(-W / 2, y, 0, 0.55, 0.48, D + 0.8, logs[(k + 1) % 2]);
      details.box(W / 2, y, 0, 0.55, 0.48, D + 0.8, logs[(k + 1) % 2]);
    }
    details.box(0, 1.6, -D / 2 - 0.3, 1.8, 2.6, 0.2, [0.33, 0.19, 0.1]);
    details.box(0.6, 1.5, -D / 2 - 0.42, 0.15, 0.15, 0.1, [0.9, 0.75, 0.3]);
    for (const x of [-4.1, 4.1]) {
      details.box(x, 2.3, -D / 2 - 0.3, 1.8, 1.4, 0.15, [0.95, 0.93, 0.88]);
      details.box(x, 2.3, -D / 2 - 0.36, 1.4, 1, 0.1, [1, 0.85, 0.45]);
    }
    // The roof: timber under a thick layer of snow, gable ends of logs, a chimney.
    const [X, E] = [W / 2 + 1, D / 2 + 1.3];
    for (const z of [-1, 1]) {
      const pitch = [0, E, z * 3.2].map((v) => v / Math.hypot(E, 3.2));
      details.quad([-X, 4.8, z * E], [X, 4.8, z * E], [X, 8, 0], [-X, 8, 0], pitch, [0.4, 0.25, 0.14]);
      details.quad([-X, 5.2, z * E], [X, 5.2, z * E], [X, 8.4, 0], [-X, 8.4, 0], pitch, snow);
      details.quad([-X, 4.8, z * E], [X, 4.8, z * E], [X, 5.2, z * E], [-X, 5.2, z * E], [0, 0, z], snow);
    }
    for (const x of [-W / 2, W / 2]) details.quad([x, 4.8, -D / 2], [x, 4.8, D / 2], [x, 8, 0], [x, 8, 0], [Math.sign(x), 0, 0], logs[0]);
    details.box(W / 4, 7.2, 2, 1, 3.5, 1, [0.55, 0.55, 0.58]);
    details.box(W / 4, 9, 2, 1.25, 0.25, 1.25, snow);
    details.box(0, 3.65, -D / 2 - 0.35, 6.6, 1, 0.12, [0.3, 0.18, 0.1]);
    const yaw = Math.atan2(s.tz, s.tx) + (side < 0 ? Math.PI : 0);
    details.moveSince(from, house[0], s.y - 0.25, house[1], yaw);
    // Read from the road, the lettering runs from +x to -x.
    const z = -D / 2 - 0.42;
    sign.quad([3.2, 3.25, z], [-3.2, 3.25, z], [-3.2, 4.05, z], [3.2, 4.05, z], [0, 0, -1], snow, [
      [SIGN_UV[0], SIGN_UV[3]], [SIGN_UV[2], SIGN_UV[3]], [SIGN_UV[2], SIGN_UV[1]], [SIGN_UV[0], SIGN_UV[1]],
    ]);
    sign.moveSince(0, house[0], s.y - 0.25, house[1], yaw);
  }

  // Scenery either side of the walls, on a fixed seed so a map always looks
  // the same — and never on another stretch of road, in a lake or a shortcut.
  let seed = map.key.length * 9301 + 49297;
  const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  const shore = (lake: [number, number, number], x: number, z: number) =>
    Math.hypot(x - lake[0], z - lake[1]) - lakeRadius(lake, Math.atan2(z - lake[1], x - lake[0]));
  const nearRoad = (x: number, z: number, r: number) =>
    samples.some((s, k) => k % 3 === 0 && (s.x - x) ** 2 + (s.z - z) ** 2 < (half + wallW + r) ** 2);
  buildLandmarks(track, prop, at);
  const trim = buildTrim(track, at, inCut, sign);
  const clearings = [
    map.campfire && [...map.campfire, 14],
    map.cabin && [...map.cabin, 9],
    map.dj && [...map.dj, 22],
    map.lawn && [...map.lawn, 19],
  ].filter((c): c is number[] => !!c);
  const crossings = track.hazards.filter((h) => h.kind === "moose" || h.kind === "ball");
  const clearOfRoad = (x: number, z: number, r: number) =>
    !nearRoad(x, z, r) &&
    !(house && Math.hypot(x - house[0], z - house[1]) < 11 + r) &&
    track.lakes.every((lake) => shore(lake, x, z) > r + 1.5) &&
    !inCut(x, z, r + 1.5);

  for (const cut of track.cuts) {
    // A dirt track between hedges, and bushes across the gap in the wall to
    // hide where it starts and ends — soft enough to drive through.
    const dirt: Rgb = [0.55, 0.42, 0.28];
    const P = (d: number, lat: number, y: number) => [cut.ax + cut.dx * d - cut.dz * lat, y, cut.az + cut.dz * d + cut.dx * lat];
    // Narrow through the gate, then the width of the path.
    for (const [from, to, w] of [[-2, GATE_LENGTH, CUT_GATE], [GATE_LENGTH, cut.len + 2, CUT_HALF]]) {
      details.quad(P(from, -w, -0.02), P(from, w, -0.02), P(to, w, -0.02), P(to, -w, -0.02), up, dirt);
    }
    for (let d = 0; d <= cut.len; d += 1.7) {
      for (const side of [-1, 1]) {
        const [x, , z] = P(d, side * (cutHalf(d) + 0.9), 0);
        if (locate(track, x, z, cut.a).dist < half + wallW) continue;
        const h = 1.6 + rand() * 0.8;
        const leaf: Rgb = rand() < 0.5 ? [0.18, 0.5, 0.2] : [0.24, 0.58, 0.22];
        details.box(x, h / 2, z, 1.9, h, 1.9, leaf);
      }
    }
    for (const end of [cut.a, cut.a + cut.skip]) {
      for (let i = end - 12; i <= end + 12; i++) {
        for (const side of SIDES) {
          const [x, , z] = at((i + n) % n, side * (half + wallW / 2), 0);
          if (!inCut(x, z) || rand() < 0.3) continue;
          const h = 1.4 + rand();
          details.box(x, h / 2, z, 1.6 + rand(), h, 1.6 + rand(), rand() < 0.5 ? [0.2, 0.55, 0.22] : [0.28, 0.62, 0.24]);
        }
      }
    }
  }

  const SEG = 48;
  for (const lake of track.lakes) {
    const [lx, lz] = lake;
    const edge = (k: number, grow: number, y: number) => {
      const a = (k / SEG) * Math.PI * 2;
      const r = lakeRadius(lake, a) + grow;
      return [lx + Math.cos(a) * r, y, lz + Math.sin(a) * r];
    };
    const centre = (y: number) => [lx, y, lz];
    for (let k = 0; k < SEG; k++) {
      // A dark bed, a sandy shore, and the surface: deep blue in the middle,
      // paler over the shallows.
      details.quad(centre(-0.04), edge(k, 0, -0.04), edge(k + 1, 0, -0.04), centre(-0.04), up, [0.12, 0.3, 0.36]);
      details.quad(edge(k, 0, -0.035), edge(k, 1.8, -0.035), edge(k + 1, 1.8, -0.035), edge(k + 1, 0, -0.035), up, [0.86, 0.78, 0.55]);
      const w = water.vert(centre(0.1), up, [0.16, 0.42, 0.72]);
      water.vert(edge(k, 0.4, 0.1), up, [0.42, 0.72, 0.88]);
      water.vert(edge(k + 1, 0.4, 0.1), up, [0.42, 0.72, 0.88]);
      water.i.push(w, w + 1, w + 2);
    }
    // Reeds in clumps along the shore, lily pads out on the water.
    for (let k = 0; k < SEG; k += 2) {
      if (rand() < 0.45) continue;
      const [x, , z] = edge(k + rand(), -0.8, 0);
      if (nearRoad(x, z, 1)) continue;
      for (let r = 0; r < 5; r++) {
        const h = 1.2 + rand() * 1.2;
        const [rx, rz] = [x + (rand() - 0.5) * 1.6, z + (rand() - 0.5) * 1.6];
        details.box(rx, h / 2, rz, 0.12, h, 0.12, [0.35, 0.55, 0.2]);
        if (rand() < 0.4) details.box(rx, h, rz, 0.2, 0.5, 0.2, [0.45, 0.28, 0.15]);
      }
    }
    for (let k = 0; k < lake[2] / 3; k++) {
      const a = rand() * Math.PI * 2;
      const r = lakeRadius(lake, a) * (0.2 + rand() * 0.65);
      const [x, z] = [lx + Math.cos(a) * r, lz + Math.sin(a) * r];
      if (nearRoad(x, z, 1)) continue;
      details.cylinder(x, 0.13, z, 0.6 + rand() * 0.5, 0.04, [0.25, 0.6, 0.25], "y", 8);
      if (rand() < 0.3) details.box(x + 0.2, 0.25, z, 0.35, 0.25, 0.35, [0.98, 0.7, 0.85]);
    }
    // A jetty out from the far shore, if it's clear of the road there.
    const a = lx * 0.37; // Somewhere round the shore, the same each time.
    const [jx, , jz] = edge(a * (SEG / (Math.PI * 2)), 2, 0);
    if (nearRoad(jx, jz, 4) || !track.lakes.every((l) => l === lake || shore(l, jx, jz) > 3)) continue;
    const [dx, dz] = [lx - jx, lz - jz].map((v) => v / Math.hypot(lx - jx, lz - jz));
    for (let d = 0; d < 9; d += 0.7) {
      details.box(jx + dx * d, 0.35, jz + dz * d, 1.4, 0.12, 1.4, [0.62, 0.45, 0.28]);
    }
    for (const d of [0.5, 4.5, 8.5]) {
      for (const s of [-1, 1]) details.box(jx + dx * d - dz * s * 0.8, 0.3, jz + dz * d + dx * s * 0.8, 0.2, 0.8, 0.2, [0.45, 0.32, 0.2]);
    }
  }

  // Deep in the forest, the trees stand twice as thick, and further back.
  const woods = th.prop === "forest";
  for (let i = 0; i < n; i += woods ? 4 : 7) {
    for (const side of woods ? [-1, 1, -1, 1] : [-1, 1]) {
      const lat = side * (half + wallW + 3 + rand() * (woods ? 42 : 30));
      const [x, , z] = at(i, lat, 0);
      const size = 0.8 + rand() * 0.8;
      if (!clearOfRoad(x, z, 2.5 * size)) continue;
      if (clearings.some(([cx, cz, r]) => Math.hypot(x - cx, z - cz) < r)) continue;
      if (crossings.some((h) => Math.abs(loopDelta(h.idx, i, n)) < 8)) continue;
      if (woods) {
        forestProp(prop(), x, z, size * 1.2, rand());
        continue;
      }
      if (th.prop === "tree") {
        meadowTree(prop(), x, z, size, rand(), rand());
        // Round the tree, a shrub or a patch of flowers.
        const [fx, fz] = [x + (rand() - 0.5) * 12, z + (rand() - 0.5) * 12];
        if (!clearOfRoad(fx, fz, 1.5)) continue;
        if (rand() < 0.35) {
          details.box(fx, 0.6, fz, 2, 1.2, 1.7, [0.22, 0.52, 0.2]);
          details.box(fx + 0.3, 1.2, fz, 1.3, 0.6, 1.2, [0.26, 0.58, 0.22]);
          continue;
        }
        const petal: Rgb = ([[0.98, 0.9, 0.3], [0.95, 0.4, 0.5], [0.98, 0.98, 0.98], [0.65, 0.5, 0.95]] as Rgb[])[Math.floor(rand() * 4)];
        for (let f = 0; f < 7; f++) {
          details.box(fx + (rand() - 0.5) * 4, 0.25, fz + (rand() - 0.5) * 4, 0.35, 0.18, 0.35, petal);
        }
      } else {
        snowPine(prop(), x, groundY(x, z), z, size, rand());
      }
    }
    // Fly agarics at the roadside, now and then.
    if (woods && i % 12 === 0 && rand() < 0.5) {
      const [x, , z] = at(i, (rand() < 0.5 ? -1 : 1) * (half + wallW + 1.5 + rand() * 3), 0);
      if (clearOfRoad(x, z, 1)) {
        prop().cylinder(x, 0.35, z, 0.2, 0.7, [0.96, 0.94, 0.88], "y", 6);
        prop().cone(x, 0.65, z, 0.75, 0.45, [0.85, 0.12, 0.1], 8);
      }
    }
  }
  // A giant moose on a plinth, beside the first long view of the lap that has room for it.
  let statue: number[] | null = null;
  if (th.prop !== "pine") {
    for (const f of [0.04, 0.3, 0.55, 0.8, 0.15, 0.68]) {
      const i = Math.floor(f * n);
      const s = samples[i];
      for (const side of [1, -1]) {
        const [x, , z] = at(i, side * (half + wallW + 13), 0);
        if (statue || !clearOfRoad(x, z, 7) || clearings.some(([cx, cz, r]) => Math.hypot(x - cx, z - cz) < r + 7)) continue;
        // Facing back down the road, turned a little towards it.
        const yaw = -Math.atan2(-s.tz - s.nz * side * 0.6, -s.tx - s.nx * side * 0.6);
        statue = [x, z, yaw];
        const from = trim.count;
        trim.box(0, 1.5, 0, 9, 3, 5, [0.72, 0.7, 0.66]);
        trim.box(0, 3.15, 0, 9.6, 0.3, 5.6, [0.84, 0.82, 0.78]);
        trim.moveSince(from, x, 0, z, -yaw);
      }
    }
  }
  return { world, road, line, pads, props, details, water, terrain, sign, forest, trim, statue };
}

const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/**
 * What makes the road read at speed: kerbs on the inside of every corner,
 * chevron boards round the outside of the sharp ones, the start grid's
 * boxes, and the arch over the line with the game's name on it (its
 * lettering into `sign`, from the atlas).
 */
function buildTrim(track: Track, at: (i: number, lat: number, y: number) => number[], inCut: (x: number, z: number) => boolean, sign: MeshBuilder) {
  const { samples, n } = track;
  const b = new MeshBuilder();
  const up = [0, 1, 0];
  const red: Rgb = [0.9, 0.14, 0.12];
  const white: Rgb = [0.97, 0.97, 0.97];
  /** How far, and which way, the road turns from `reach` samples before i to `reach` after: + towards +lat. */
  const turn = (i: number, reach: number) => {
    const a = samples[(i - reach + n) % n];
    const c = samples[(i + reach) % n];
    const s = samples[i];
    const angle = Math.acos(Math.max(-1, Math.min(1, a.tx * c.tx + a.tz * c.tz)));
    return angle * Math.sign((c.tx - a.tx) * s.nx + (c.tz - a.tz) * s.nz);
  };

  // Kerbs, red and white two units at a time, on the inside of every corner.
  for (let i = 0; i < n; i++) {
    const t = turn(i, 4);
    if (Math.abs(t) < 0.2) continue;
    const side = Math.sign(t);
    const h = samples[i].half;
    const [gx, , gz] = at(i, side * h, 0);
    if (inCut(gx, gz)) continue;
    const col = Math.floor(i / 2) % 2 ? red : white;
    const [l0, l1] = [side * (h - 1.1), side * (h - 0.05)];
    b.quad(at(i, l0, 0.06), at(i, l1, 0.06), at(i + 1, l1, 0.06), at(i + 1, l0, 0.06), up, col);
    b.quad(at(i, l0, 0), at(i, l0, 0.06), at(i + 1, l0, 0.06), at(i + 1, l0, 0), [-side * samples[i].nx, 0, -side * samples[i].nz], col);
  }

  // Chevron boards round the outside of the sharp corners, pointing the way.
  for (let i = 0; i < n; i++) {
    const t = turn(i, 6);
    if (Math.abs(t) < 0.6 || Math.abs(turn((i + 1) % n, 6)) > Math.abs(t) || Math.abs(turn((i - 1 + n) % n, 6)) >= Math.abs(t)) continue;
    const inner = Math.sign(t);
    for (const k of [-12, -6, 0]) {
      const j = (i + k + n) % n;
      const s = samples[j];
      const [x, y, z] = at(j, -inner * (s.half + 2.3), 0);
      // Not on some other stretch of road.
      if (inCut(x, z) || samples.some((q, m) => Math.abs(loopDelta(m, j, n)) > 25 && Math.hypot(q.x - x, q.z - z) < q.half + 2)) continue;
      const from = b.count;
      // Built facing -x (back down the road), +z across it towards the inside.
      for (const pz of [-0.6, 0.6]) b.box(0.05, 0.75, pz, 0.12, 1.5, 0.12, [0.6, 0.62, 0.66]);
      b.box(0, 1.95, 0, 0.1, 1.1, 1.9, white);
      b.box(0.02, 1.95, 0, 0.1, 1.2, 2, [0.15, 0.15, 0.18]);
      const arm = (z0: number, y0: number, z1: number, y1: number) => {
        const w = 0.42 * inner;
        b.quad([-0.07, y0, z0], [-0.07, y0, z0 + w], [-0.07, y1, z1 + w], [-0.07, y1, z1], [-1, 0, 0], red);
      };
      for (const dz of [-0.45, 0.25]) {
        arm(inner * (dz - 0.25), 2.35, inner * (dz + 0.15), 1.95);
        arm(inner * (dz + 0.15), 1.95, inner * (dz - 0.25), 1.55);
      }
      b.moveSince(from, x, y, z, Math.atan2(s.tz, s.tx));
    }
  }

  // The start grid: a white box on the road behind where each kart lines up (sim.ts, createRace).
  for (let k = 0; k < 8; k++) {
    const s = samples[n - 5 - Math.floor(k / 2) * 5];
    const lat = k % 2 ? 3 : -3;
    const P = (along: number, l: number) => [s.x + s.tx * along + s.nx * l, s.y + 0.02, s.z + s.tz * along + s.nz * l];
    const bar = (a0: number, a1: number, l0: number, l1: number) => b.quad(P(a0, l0), P(a0, l1), P(a1, l1), P(a1, l0), up, white);
    bar(-1.75, -1.5, lat - 1.3, lat + 1.3);
    bar(-1.75, 0.7, lat - 1.3, lat - 1.08);
    bar(-1.75, 0.7, lat + 1.08, lat + 1.3);
  }

  // The arch over the line: red-and-white pillars, a navy beam, the name on both faces.
  {
    const s = samples[1];
    const w = s.half + 1;
    const from = b.count;
    for (const side of [-1, 1]) {
      for (let k = 0; k < 7; k++) b.box(0, 0.5 + k, side * w, 1, 1, 1, k % 2 ? white : red);
      b.box(0, 7.2, side * w, 1.2, 0.4, 1.2, [0.98, 0.78, 0.15]);
    }
    b.box(0, 6.9, 0, 0.8, 1.6, 2 * w + 1.2, [0.1, 0.14, 0.34]);
    b.moveSince(from, s.x, s.y, s.z, Math.atan2(s.tz, s.tx));
    const signFrom = sign.count;
    const [u0, v0, u1, v1] = LOGO_UV;
    const [y0, y1, half] = [6.3, 7.5, 4.8];
    sign.quad([-0.42, y0, -half], [-0.42, y0, half], [-0.42, y1, half], [-0.42, y1, -half], [-1, 0, 0], white, [[u0, v1], [u1, v1], [u1, v0], [u0, v0]]);
    sign.quad([0.42, y0, half], [0.42, y0, -half], [0.42, y1, -half], [0.42, y1, half], [1, 0, 0], white, [[u0, v1], [u1, v1], [u1, v0], [u0, v0]]);
    sign.moveSince(signFrom, s.x, s.y, s.z, Math.atan2(s.tz, s.tx));
  }
  return b;
}
/** A meadow tree, by `kind`, 0…1: a blocky oak, a round lime, or a tall poplar; `shade` picks its green. */
function meadowTree(p: MeshBuilder, x: number, z: number, size: number, kind: number, shade: number) {
  const bark: Rgb = [0.5, 0.33, 0.2];
  const leaf: Rgb = shade < 0.5 ? [0.2, 0.55, 0.22] : [0.3, 0.65, 0.2];
  const light: Rgb = [leaf[0] * 1.25, leaf[1] * 1.2, leaf[2] * 1.15];
  if (kind < 0.45) {
    p.cylinder(x, 1 * size, z, 0.35 * size, 2 * size, bark, "y", 6);
    p.box(x, 2.8 * size, z, 2.8 * size, 2.2 * size, 2.8 * size, leaf);
    p.box(x, 4.1 * size, z, 1.8 * size, 1 * size, 1.8 * size, light);
  } else if (kind < 0.8) {
    // Round, darker underneath and lighter on top.
    p.cylinder(x, 1.1 * size, z, 0.3 * size, 2.2 * size, bark, "y", 6);
    p.sphere(x, 3.3 * size, z, 1.8 * size, (ring) => (ring < 2 ? [leaf[0] * 0.8, leaf[1] * 0.8, leaf[2] * 0.8] : ring > 3 ? light : leaf), 6, 9);
  } else {
    p.cylinder(x, 1 * size, z, 0.25 * size, 2 * size, bark, "y", 5);
    p.box(x, 3.4 * size, z, 1.3 * size, 3.2 * size, 1.3 * size, leaf);
    p.box(x, 5.4 * size, z, 0.9 * size, 1.2 * size, 0.9 * size, light);
  }
}

/** A pine in the snow, by `kind`, 0…1: three tiers capped white, a taller slimmer one, or one the snow's slid off. */
function snowPine(p: MeshBuilder, x: number, y: number, z: number, size: number, kind: number) {
  const snow: Rgb = [0.95, 0.97, 1];
  const [a, b]: Rgb[] = kind < 0.85 ? [[0.12, 0.42, 0.3], [0.14, 0.48, 0.34]] : [[0.1, 0.36, 0.26], [0.12, 0.42, 0.3]];
  if (kind < 0.6) {
    p.cylinder(x, y + 0.6 * size, z, 0.3 * size, 1.2 * size, [0.45, 0.3, 0.2], "y", 6);
    p.cone(x, y + 1.1 * size, z, 1.9 * size, 2.6 * size, a);
    p.cone(x, y + 2.6 * size, z, 1.4 * size, 2.2 * size, b);
    p.cone(x, y + 4.1 * size, z, 0.8 * size, 1.4 * size, snow);
    return;
  }
  p.cylinder(x, y + 0.7 * size, z, 0.25 * size, 1.4 * size, [0.45, 0.3, 0.2], "y", 6);
  for (let t = 0; t < 4; t++) p.cone(x, y + (1.2 + t * 1.5) * size, z, (1.4 - t * 0.28) * size, 2.2 * size, t % 2 ? b : a, 7);
  if (kind < 0.85) p.cone(x, y + 6.2 * size, z, 0.5 * size, 1 * size, snow, 7);
}

/** A tree of the Swedish forest — spruce, pine or birch — or a mossy boulder, by `kind`, 0…1. */
function forestProp(p: MeshBuilder, x: number, z: number, size: number, kind: number) {
  const bark: Rgb = [0.4, 0.27, 0.17];
  if (kind < 0.55) {
    const [a, b]: Rgb[] = [[0.09, 0.28, 0.16], [0.12, 0.34, 0.2]];
    p.cylinder(x, 0.6 * size, z, 0.3 * size, 1.2 * size, bark, "y", 5);
    p.cone(x, 0.9 * size, z, 2.2 * size, 3.2 * size, a, 7);
    p.cone(x, 2.8 * size, z, 1.7 * size, 2.9 * size, b, 7);
    p.cone(x, 4.7 * size, z, 1.1 * size, 2.6 * size, a, 7);
  } else if (kind < 0.72) {
    const crown: Rgb = [0.16, 0.38, 0.18];
    p.cylinder(x, 2.6 * size, z, 0.28 * size, 5.2 * size, [0.66, 0.38, 0.22], "y", 5);
    p.cone(x, 4.4 * size, z, 1.9 * size, 1.9 * size, crown, 7);
    p.cone(x, 5.6 * size, z, 1.3 * size, 1.8 * size, crown, 7);
  } else if (kind < 0.93) {
    p.cylinder(x, 2.2 * size, z, 0.24 * size, 4.4 * size, [0.93, 0.92, 0.88], "y", 6);
    for (const y of [1.1, 2.2, 3.1]) p.box(x + 0.06 * size, y * size, z, 0.52 * size, 0.1 * size, 0.3 * size, [0.16, 0.16, 0.16]);
    p.box(x, 4.4 * size, z, 2.6 * size, 2 * size, 2.6 * size, [0.45, 0.7, 0.26]);
    p.box(x + 0.4 * size, 5.5 * size, z - 0.3 * size, 1.7 * size, 1.2 * size, 1.7 * size, [0.52, 0.76, 0.3]);
  } else {
    p.box(x, 0.6 * size, z, 2.4 * size, 1.2 * size, 1.9 * size, [0.52, 0.53, 0.55]);
    p.box(x, 1.25 * size, z, 1.8 * size, 0.2 * size, 1.4 * size, [0.35, 0.52, 0.25]);
  }
}

/** Croquet's colours: blue, red, black, yellow. */
const CROQUET: Rgb[] = [[0.15, 0.35, 0.85], [0.85, 0.15, 0.12], [0.12, 0.12, 0.14], [0.98, 0.8, 0.12]];

/**
 * What stands still on and beside a map's road: puddles and slush on it,
 * croquet hoops over its pads; the campfire, the cabin, the DJ's stage and
 * the croquet lawn beside it; warning signs before moose and
 * mosquitoes.
 */
function buildLandmarks(track: Track, prop: () => MeshBuilder, at: (i: number, lat: number, y: number) => number[]) {
  const { samples, map } = track;
  const white: Rgb = [0.97, 0.97, 0.97];
  const bark: Rgb = [0.4, 0.27, 0.15];
  const wood: Rgb = [0.78, 0.62, 0.4];
  const slush: Rgb = [0.35, 0.68, 1];
  const cup: Rgb = [0.86, 0.1, 0.12];
  // Along the road and across it, from a sample: for laying things out by the road.
  const local = (i: number, along: number, lat: number) => {
    const s = samples[((i % track.n) + track.n) % track.n];
    return [s.x + s.tx * along + s.nx * lat, s.z + s.tz * along + s.nz * lat];
  };

  track.pads.forEach((pad, k) => {
    if (!map.wickets) return;
    // A giant croquet hoop over the pad: white posts, a coloured bar across.
    const col = CROQUET[k % CROQUET.length];
    const [l0, l1] = [-1, 1].map((side) => pad.lat + side * (PAD_WIDTH / 2 + POST_RADIUS));
    const [ax, , az] = at(pad.idx, l0, 0);
    const [bx, , bz] = at(pad.idx, l1, 0);
    prop().cylinder(ax, 1.4, az, POST_RADIUS, 2.8, white, "y", 8);
    prop().cylinder(bx, 1.4, bz, POST_RADIUS, 2.8, white, "y", 8);
    prop().log(ax, az, bx, bz, POST_RADIUS + 0.05, col, col, 2.9, 8);
  });

  // On a hilly road, lift what was just built onto it, each point by the road's height there.
  const lift = (p: MeshBuilder, from: number, hint: number) => {
    for (let o = from * FLOATS_PER_VERTEX; o < p.v.length; o += FLOATS_PER_VERTEX) {
      p.v[o + 1] += roadY(track, locate(track, p.v[o], p.v[o + 2], hint).idx, p.v[o], p.v[o + 2]);
    }
  };

  let wobble = 0;
  for (const h of track.hazards) {
    const p = prop();
    const from = p.count;
    wobble += 1.7;
    if (h.kind === "pool") {
      const [x, , z] = at(h.idx, h.lat, 0);
      p.blob(x, z, POOL_RADIUS * 1.2, 0.02, [0.36, 0.27, 0.18], 0.12, 18, wobble);
      p.blob(x, z, POOL_RADIUS, 0.035, [0.3, 0.48, 0.6], 0.1, 18, wobble);
      p.blob(x - 0.5, z + 0.3, POOL_RADIUS * 0.35, 0.04, [0.6, 0.78, 0.88], 0.2, 10, wobble);
    } else if (h.kind === "slush") {
      // Blue slush across the road, in blobs, and the red cups it came out of.
      for (const [u, v, r] of [[0, 0, 3.2], [-2.8, 1, 2.4], [2.8, -0.8, 2.5], [1, 2, 1.8], [-1.2, -2, 1.8]]) {
        const [x, z] = local(h.idx, u, h.lat + v);
        p.blob(x, z, r, 0.035 + r * 0.001, slush, 0.15, 14, u + v);
      }
      const [x, z] = local(h.idx, 0.5, h.lat - 0.5);
      p.blob(x, z, 1.1, 0.045, [0.65, 0.88, 1], 0.2, 10);
      for (const [u, v, a] of [[-4.5, 3.4, 0.4], [3.6, 2.8, 2.2], [-1.8, -3.2, 4.2], [5, -1.5, 5.6], [0.8, 3.6, 1.2]]) {
        const [x0, z0] = local(h.idx, u, h.lat + v);
        const [dx, dz] = [Math.cos(a) * 1.1, Math.sin(a) * 1.1];
        // On its side: red, with the white rim at the open end — and what was
        // in it running out of there.
        p.log(x0, z0, x0 + dx, z0 + dz, 0.42, cup, cup, 0.42, 8);
        p.log(x0 + dx, z0 + dz, x0 + dx * 1.08, z0 + dz * 1.08, 0.46, white, white, 0.42, 8);
        p.blob(x0 + dx * 1.9, z0 + dz * 1.9, 0.8, 0.04, slush, 0.25, 10, a);
      }
      for (const [u, v] of [[-6, -5.5], [6.5, 5.6]]) {
        const [x0, z0] = local(h.idx, u, h.lat + v);
        p.cylinder(x0, 0.55, z0, 0.42, 1.1, cup, "y", 8);
        p.cylinder(x0, 1.12, z0, 0.46, 0.06, white, "y", 8);
      }
    } else if (h.kind === "timber") {
      // Cut logs stacked in a pyramid, along the road, their ends pale.
      const rows = [6, 5, 4];
      rows.forEach((count, row) => {
        const r = TIMBER_WIDTH / rows[0];
        for (let k = 0; k < count; k++) {
          const lat = h.lat + (k - (count - 1) / 2) * r * 2;
          const [ax, az] = local(h.idx, -TIMBER_LENGTH, lat);
          const [bx, bz] = local(h.idx, TIMBER_LENGTH, lat);
          const shade = 0.9 + 0.2 * (((k + row) * 7) % 3) / 2;
          p.log(ax, az, bx, bz, r, [0.42 * shade, 0.28 * shade, 0.16 * shade], [0.86, 0.72, 0.5], r + row * r * 1.7, 8);
        }
      });
    } else if (h.kind === "moose" || h.kind === "swarm") {
      // A yellow warning triangle, facing whoever's coming.
      const i = h.idx - 22;
      const [x, z] = local(i, 0, track.half + 2.6);
      const t = samples[((i % track.n) + track.n) % track.n];
      p.box(x, 1.2, z, 0.18, 2.4, 0.18, [0.6, 0.62, 0.66]);
      const tri = (w: number, hgt: number, back: number, col: Rgb) => {
        const [cx, cz] = [x - t.tx * back, z - t.tz * back];
        const corner = (u: number, v: number) => [cx + t.nx * u, 3.2 + v, cz + t.nz * u];
        p.quad(corner(-w, -hgt / 3), corner(w, -hgt / 3), corner(0, (2 * hgt) / 3), corner(0, (2 * hgt) / 3), [-t.tx, 0, -t.tz], col);
      };
      tri(1.35, 2.3, 0.12, [0.85, 0.12, 0.1]);
      tri(1.05, 1.8, 0.2, [0.98, 0.82, 0.15]);
      // The warning itself: a black exclamation mark in the middle of it.
      const bar = (w: number, v0: number, v1: number) => {
        const [cx, cz] = [x - t.tx * 0.3, z - t.tz * 0.3];
        const corner = (u: number, v: number) => [cx + t.nx * u, 3.2 + v, cz + t.nz * u];
        p.quad(corner(-w, v0), corner(w, v0), corner(w, v1), corner(-w, v1), [-t.tx, 0, -t.tz], [0.1, 0.1, 0.12]);
      };
      bar(0.11, 0.05, 0.8);
      bar(0.11, -0.4, -0.2);
    }
    lift(p, from, h.idx);
  }

  if (map.campfire) {
    const [x, z] = map.campfire;
    const p = prop();
    // A big fire: a ring of stones, a stack of logs burning in it.
    for (let k = 0; k < 14; k++) {
      const a = (k / 14) * Math.PI * 2;
      p.box(x + Math.cos(a) * 2.6, 0.35, z + Math.sin(a) * 2.6, 1, 0.7, 0.9, [0.5, 0.5, 0.52]);
    }
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI;
      const [dx, dz] = [Math.cos(a) * 2, Math.sin(a) * 2];
      p.log(x - dx, z - dz, x + dx, z + dz, 0.3, [0.22, 0.15, 0.1], [0.9, 0.4, 0.1], 0.3 + k * 0.22);
    }
    // Logs to sit on, round the fire, and a tent.
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + 0.4;
      const [cx, cz] = [x + Math.cos(a) * 5.5, z + Math.sin(a) * 5.5];
      const [dx, dz] = [-Math.sin(a) * 1.8, Math.cos(a) * 1.8];
      p.log(cx - dx, cz - dz, cx + dx, cz + dz, 0.5, bark, wood);
    }
    const [tx, tz] = [x - 9, z + 6];
    const orange: Rgb = [0.95, 0.5, 0.15];
    p.quad([tx - 1.8, 0, tz - 2], [tx, 2.4, tz - 2], [tx, 2.4, tz + 2], [tx - 1.8, 0, tz + 2], [-0.8, 0.6, 0], orange);
    p.quad([tx + 1.8, 0, tz - 2], [tx, 2.4, tz - 2], [tx, 2.4, tz + 2], [tx + 1.8, 0, tz + 2], [0.8, 0.6, 0], [0.85, 0.42, 0.1]);
    p.quad([tx - 1.8, 0, tz - 2], [tx + 1.8, 0, tz - 2], [tx, 2.4, tz - 2], [tx, 2.4, tz - 2], [0, 0, -1], [0.3, 0.2, 0.1]);
    p.quad([tx - 1.8, 0, tz + 2], [tx + 1.8, 0, tz + 2], [tx, 2.4, tz + 2], [tx, 2.4, tz + 2], [0, 0, 1], orange);
  }

  if (map.cabin) {
    // A red cottage with white corners, as every Swedish lake has.
    const [x, z] = map.cabin;
    const p = prop();
    const falu: Rgb = [0.62, 0.13, 0.1];
    const roof: Rgb = [0.18, 0.18, 0.2];
    p.box(x, 1.8, z, 7, 3.6, 5, falu);
    for (const dx of [-3.5, 3.5]) for (const dz of [-2.5, 2.5]) p.box(x + dx, 1.8, z + dz, 0.3, 3.6, 0.3, white);
    p.quad([x - 3.5, 3.6, z - 2.5], [x + 3.5, 3.6, z - 2.5], [x + 3.5, 3.6, z + 2.5], [x - 3.5, 3.6, z + 2.5], [0, 1, 0], falu);
    for (const side of [-1, 1]) {
      p.quad([x - 3.9, 3.4, z + side * 3], [x + 3.9, 3.4, z + side * 3], [x + 3.9, 5.6, z], [x - 3.9, 5.6, z], [0, 0.8, side * 0.6], roof);
      p.quad([x + side * 3.5, 3.6, z - 2.5], [x + side * 3.5, 3.6, z + 2.5], [x + side * 3.5, 5.5, z], [x + side * 3.5, 5.5, z], [side, 0, 0], falu);
    }
    p.box(x + 1.5, 6, z + 1, 0.7, 1.6, 0.7, falu);
    // The door and windows face the lake, east.
    p.box(x + 3.55, 1.1, z, 0.1, 2.2, 1.1, white);
    p.box(x + 3.58, 1.05, z, 0.1, 2, 0.9, [0.2, 0.35, 0.3]);
    for (const dz of [-1.6, 1.6]) {
      p.box(x + 3.55, 2.1, z + dz, 0.1, 1.1, 1.1, white);
      p.box(x + 3.58, 2.1, z + dz, 0.1, 0.8, 0.8, [0.55, 0.75, 0.9]);
    }
  }

  if (map.dj) {
    // The stage, facing south to the road: the booth and its decks, speaker
    // stacks either side, a truss over it all for the lights and the disco
    // ball. What moves and glows is drawn each frame.
    const [x, z] = map.dj;
    const p = prop();
    const from = p.count;
    const black: Rgb = [0.12, 0.12, 0.14];
    const metal: Rgb = [0.72, 0.74, 0.78];
    // Deep enough to stand level on a mountainside — and there, a wooden
    // terrace out in front for the dancers.
    p.box(x, -3.5, z, 16, 9, 8, [0.22, 0.2, 0.26]);
    if (map.alpine) p.box(x, -4, z - 9, 14, 8, 10, [0.6, 0.42, 0.26]);
    p.box(x, 1.6, z - 1.5, 5, 1.2, 1.4, black);
    p.box(x, 1.6, z - 2.22, 4.6, 0.8, 0.05, [0.55, 0.15, 0.8]);
    for (const dx of [-1.4, 1.4]) {
      p.cylinder(x + dx, 2.25, z - 1.5, 0.5, 0.08, [0.3, 0.3, 0.32], "y", 12);
      p.cylinder(x + dx, 2.3, z - 1.5, 0.12, 0.06, [0.9, 0.2, 0.3], "y", 8);
    }
    for (const side of [-1, 1]) {
      const sx = x + side * 6.4;
      p.box(sx, 3, z - 1, 2.4, 4, 2.2, black);
      for (const [y, r] of [[2, 0.8], [3.8, 0.5]]) p.cylinder(sx, y, z - 2.12, r, 0.08, [0.4, 0.4, 0.44], "z", 12);
      p.box(x + side * 7.6, 4.5, z + 2, 0.4, 9, 0.4, metal);
    }
    p.box(x, 8.8, z + 2, 15.6, 0.4, 0.4, metal);
    p.box(x, 7.8, z + 2, 0.05, 2, 0.05, metal);
    p.moveSince(from, 0, terrainY(track, x, z), 0);
  }

  if (map.kubbPitch) {
    // A kubb pitch: two baselines of five blocks, the king in the middle, batons in the grass.
    const [x, z] = map.kubbPitch;
    const p = prop();
    p.quad([x - 10, -0.03, z - 6], [x + 10, -0.03, z - 6], [x + 10, -0.03, z + 6], [x - 10, -0.03, z + 6], [0, 1, 0], [0.42, 0.66, 0.3]);
    const kubb: Rgb = [0.86, 0.73, 0.5];
    for (const bx of [x - 9, x + 9]) for (let k = 0; k < 5; k++) p.box(bx, 0.45, z - 4 + k * 2, 0.4, 0.9, 0.4, kubb);
    p.box(x, 0.8, z, 0.7, 1.6, 0.7, kubb);
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) p.box(x + dx * 0.25, 1.7, z + dz * 0.25, 0.18, 0.2, 0.18, kubb);
    for (const [dx, dz, a] of [[-5, 3, 0.4], [4, -2, 2], [6, 4, 1.1]]) {
      p.log(x + dx, z + dz, x + dx + Math.cos(a) * 1.3, z + dz + Math.sin(a) * 1.3, 0.1, [0.75, 0.6, 0.38]);
    }
  }

  if (map.lawn) {
    // A mown croquet lawn: stripes, the winning peg, hoops, balls and a mallet.
    const [x, z] = map.lawn;
    const p = prop();
    for (let k = 0; k < 8; k++) {
      const x0 = x - 11 + k * 2.75;
      p.quad([x0, -0.03, z - 13], [x0 + 2.75, -0.03, z - 13], [x0 + 2.75, -0.03, z + 13], [x0, -0.03, z + 13], [0, 1, 0], k % 2 ? [0.4, 0.66, 0.3] : [0.46, 0.74, 0.34]);
    }
    CROQUET.forEach((col, k) => p.cylinder(x, 0.3 + k * 0.4, z, 0.2, 0.4, col, "y", 8));
    for (const [hx, hz] of [[x - 6, z - 8], [x + 6, z - 8], [x - 6, z + 8], [x + 6, z + 8]]) {
      p.cylinder(hx - 0.6, 0.5, hz, 0.08, 1, white, "y", 6);
      p.cylinder(hx + 0.6, 0.5, hz, 0.08, 1, white, "y", 6);
      p.log(hx - 0.6, hz, hx + 0.6, hz, 0.08, white, white, 1, 4);
    }
    CROQUET.forEach((col, k) => p.sphere(x - 4 + k * 2.6, 0.5, z + 3 + (k % 2) * 3, 0.5, () => col, 5, 8));
    p.log(x + 2, z - 3, x + 5, z - 1, 0.1, wood);
    p.log(x + 5 - 0.3, z - 1 - 0.6, x + 5 + 0.3, z - 1 + 0.6, 0.3, wood, CROQUET[0], 0.3, 8);
  }

}

/** A branch lying across the road, along z: a log with a few twigs in leaf. */
function branchMesh() {
  const b = new MeshBuilder();
  const bark: Rgb = [0.42, 0.28, 0.17];
  const leaf: Rgb = [0.3, 0.6, 0.22];
  b.cylinder(0, 0.28, 0, 0.28, BRANCH_WIDTH, bark, "z", 8);
  for (const [z, s] of [[-1.2, 1], [0.6, -1], [1.7, 1]]) {
    b.box(s * 0.5, 0.35, z, 0.9, 0.12, 0.12, bark);
    b.box(s * 1.05, 0.4, z, 0.45, 0.3, 0.5, leaf);
  }
  return b;
}

/** A snowman, facing +x: three balls of snow, coal eyes and buttons, a carrot, a hat and a scarf. */
function snowmanMesh() {
  const b = new MeshBuilder();
  const snow: Rgb = [0.97, 0.98, 1];
  const coal: Rgb = [0.1, 0.1, 0.12];
  b.sphere(0, 0.75, 0, 0.9, snow);
  b.sphere(0, 1.95, 0, 0.62, snow);
  b.sphere(0, 2.85, 0, 0.42, snow);
  b.cylinder(0, 2.42, 0, 0.5, 0.18, [0.85, 0.15, 0.15], "y");
  b.box(0.35, 2.2, 0.35, 0.2, 0.5, 0.18, [0.85, 0.15, 0.15]);
  b.box(0.55, 2.85, 0, 0.45, 0.1, 0.1, [0.98, 0.5, 0.1]);
  for (const z of [-0.14, 0.14]) b.box(0.38, 3, z, 0.08, 0.09, 0.09, coal);
  for (const y of [1.75, 2.05, 2.35]) b.box(0.6, y - 0.1, 0, 0.08, 0.1, 0.1, coal);
  b.cylinder(0, 3.22, 0, 0.42, 0.06, coal, "y");
  b.cylinder(0, 3.45, 0, 0.3, 0.5, coal, "y");
  return b;
}

/** A skier, facing +x, skis and all. */
function skierMesh(jacket: Rgb) {
  const b = new MeshBuilder();
  const dark: Rgb = [0.15, 0.17, 0.25];
  for (const z of [-0.2, 0.2]) {
    b.box(0, 0.04, z, 2.2, 0.06, 0.14, [0.9, 0.2, 0.15]);
    b.box(1.1, 0.12, z, 0.2, 0.12, 0.14, [0.9, 0.2, 0.15]);
    b.box(0, 0.2, z, 0.3, 0.3, 0.2, dark);
    b.box(0.05, 0.7, z, 0.22, 0.8, 0.2, dark);
    b.box(0.1, 0.65, z * 3, 0.05, 1.3, 0.05, [0.7, 0.72, 0.76]);
  }
  b.box(0.08, 1.4, 0, 0.45, 0.8, 0.62, jacket);
  for (const z of [-0.38, 0.38]) b.box(0.12, 1.3, z, 0.2, 0.6, 0.16, jacket);
  b.box(0.1, 2.0, 0, 0.36, 0.4, 0.36, [0.96, 0.8, 0.66]);
  b.box(0.3, 2.05, 0, 0.1, 0.14, 0.34, [0.2, 0.2, 0.24]);
  b.box(0.08, 2.26, 0, 0.4, 0.2, 0.4, jacket);
  b.box(0.08, 2.44, 0, 0.18, 0.18, 0.18, [0.97, 0.97, 0.97]);
  return b;
}

/** A flame along +x from the origin: a bulging cone, v running base to tip. */
function flameMesh() {
  const b = new MeshBuilder();
  const rings = 7;
  const sides = 14;
  const ring = (t: number) => 0.2 * Math.pow(1 - t, 0.8) * (1 + 0.5 * Math.sin(t * Math.PI));
  for (let i = 0; i < rings - 1; i++) {
    for (let k = 0; k < sides; k++) {
      const [t0, t1] = [i / (rings - 1), (i + 1) / (rings - 1)];
      const [a0, a1] = [(k / sides) * Math.PI * 2, ((k + 1) / sides) * Math.PI * 2];
      const p = (t: number, a: number) => [t, Math.cos(a) * ring(t), Math.sin(a) * ring(t)];
      const n = [0, Math.cos((a0 + a1) / 2), Math.sin((a0 + a1) / 2)];
      b.quad(p(t0, a0), p(t0, a1), p(t1, a1), p(t1, a0), n, [1, 1, 1], [
        [k / sides, t0], [(k + 1) / sides, t0], [(k + 1) / sides, t1], [k / sides, t1],
      ]);
    }
  }
  return b;
}

/** A duck, facing +x — a mallard drake, or a yellow duckling — without its wings. */
function duckMesh(chick: boolean) {
  const b = new MeshBuilder();
  const yellow: Rgb = [1, 0.84, 0.25];
  const body: Rgb = chick ? yellow : [0.62, 0.55, 0.47];
  const orange: Rgb = [0.98, 0.55, 0.1];
  b.box(0, 0.42, 0, 0.9, 0.42, 0.55, body);
  b.box(0.35, 0.45, 0, 0.3, 0.36, 0.5, chick ? yellow : [0.45, 0.25, 0.18]);
  b.box(-0.5, 0.55, 0, 0.25, 0.16, 0.36, chick ? yellow : [0.22, 0.22, 0.25]);
  b.box(0.42, 0.82, 0, 0.3, 0.3, 0.28, chick ? yellow : [0.08, 0.42, 0.22]);
  if (!chick) b.box(0.4, 0.66, 0, 0.24, 0.05, 0.3, [0.97, 0.97, 0.97]);
  b.box(0.64, 0.78, 0, 0.2, 0.07, 0.18, chick ? orange : [0.95, 0.78, 0.15]);
  for (const side of [-1, 1]) {
    b.box(0.5, 0.88, side * 0.145, 0.06, 0.06, 0.02, [0.05, 0.05, 0.05]);
    b.box(0, 0.14, side * 0.12, 0.05, 0.16, 0.05, orange);
    b.box(0.07, 0.03, side * 0.12, 0.22, 0.04, 0.14, orange);
  }
  return b;
}

/** A duck's right wing, hinged along x at the origin and reaching out along +z. */
function wingMesh(chick: boolean) {
  const b = new MeshBuilder();
  b.box(-0.08, 0, 0.14, 0.55, 0.07, 0.3, chick ? [1, 0.8, 0.22] : [0.5, 0.43, 0.36]);
  if (!chick) b.box(-0.12, 0.04, 0.16, 0.18, 0.02, 0.18, [0.2, 0.35, 0.85]);
  return b;
}

/** Spray thrown up off a wheel in water: a crown of sheets, pale blue at the foot, white at the tips. */
function splashMesh() {
  const b = new MeshBuilder();
  const petals = 11;
  for (let k = 0; k < petals; k++) {
    const a = (k / petals) * Math.PI * 2;
    const [c, s] = [Math.cos(a), Math.sin(a)];
    const h = 0.9 + 0.2 * (k % 3);
    const P = (r: number, w: number, y: number) => [c * r - s * w, y, s * r + c * w];
    const n = [c * 0.8, 0.6, s * 0.8];
    const foot: Rgb = [0.62, 0.8, 0.95];
    const tip: Rgb = [1, 1, 1];
    const v = b.vert(P(0.2, -0.16, 0), n, foot);
    b.vert(P(0.2, 0.16, 0), n, foot);
    b.vert(P(0.75, 0.07, h), n, tip);
    b.vert(P(0.75, -0.07, h), n, tip);
    b.i.push(v, v + 1, v + 2, v, v + 2, v + 3);
  }
  return b;
}

function boxMesh() {
  const b = new MeshBuilder();
  const hues: Rgb[] = [[1, 0.35, 0.35], [1, 0.8, 0.2], [0.3, 0.85, 0.4], [0.3, 0.6, 1], [0.8, 0.4, 1], [1, 0.5, 0.8]];
  const s = 0.7;
  b.quad([-s, s, -s], [s, s, -s], [s, s, s], [-s, s, s], [0, 1, 0], hues[0]);
  b.quad([-s, -s, -s], [s, -s, -s], [s, -s, s], [-s, -s, s], [0, -1, 0], hues[1]);
  b.quad([s, -s, -s], [s, s, -s], [s, s, s], [s, -s, s], [1, 0, 0], hues[2]);
  b.quad([-s, -s, -s], [-s, s, -s], [-s, s, s], [-s, -s, s], [-1, 0, 0], hues[3]);
  b.quad([-s, -s, s], [s, -s, s], [s, s, s], [-s, s, s], [0, 0, 1], hues[4]);
  b.quad([-s, -s, -s], [s, -s, -s], [s, s, -s], [-s, s, -s], [0, 0, -1], hues[5]);
  return b;
}

/** Where each kart's exhausts end, and which way they point (kart axes). */
const EXHAUSTS: Record<KartKind | "box", { at: number[]; angle: number }[]> = {
  // Flatter than the pipes themselves: seen from the chase camera, a flame
  // pointing up the screen would be foreshortened to nothing.
  classic: [0.24, -0.24].map((z) => ({ at: [-1.4, 0.86, z], angle: Math.atan2(0.28, -0.96) })),
  buggy: [{ at: [-1.42, 1.08, -0.2], angle: Math.atan2(0.34, -0.94) }],
  // Out of the tops of the brute's twin stacks, bent back.
  brute: [0.3, -0.3].map((z) => ({ at: [-1.12, 1.62, z], angle: Math.atan2(0.5, -0.87) })),
  racer: [{ at: [-1.5, 0.5, 0], angle: Math.atan2(0.12, -0.99) }],
  // Out of the back of the outboard's cowl.
  boat: [{ at: [-1.62, 0.8, 0], angle: Math.atan2(0.2, -0.98) }],
  // The box kart that stands in while the models load.
  box: [{ at: [-1.3, 0.75, 0], angle: Math.PI }],
};

/** An item's model's name, built once per kind rather than each frame. */
const ITEM_MODEL: Record<string, string> = {};
const itemModel = (kind: string) => (ITEM_MODEL[kind] ??= `item-${kind}`);

/** The item models every race loads, whoever is driving. */
const ITEM_MODELS = [
  "item-beer", "item-barf", "item-bearnaise", "item-glove", "item-can", "item-mosquito", "item-smoke", "item-soundboks", "item-cart",
  "item-moose", "item-box", "item-mark", "item-star",
];

/**
 * The chase camera: how high, how far behind, and the point it looks at — how
 * far ahead, and how high; its vertical field of view (degrees, or null for
 * the old look's, widened until the road fits across a portrait screen), and
 * how much a boost widens it (degrees). The new look's is the phone kart
 * games' long lens: from further back, narrower, so your kart is the frame's
 * hero — its back axle about 27 % of a portrait screen's width, its tyres 80 %
 * of the way down and the horizon 40 % (REVAMP_LOG.md, phase 2) — and it rolls
 * a few degrees into a turn and swings out in a drift.
 */
const CAM = LOOK === "new"
  ? { height: 6.7, back: 13.9, ahead: 12, look: 3.45, fov: 64, boostFov: 7, roll: 3, swing: 0.9 }
  : { height: 5.6, back: 8, ahead: 10, look: 0, fov: null, boostFov: 0, roll: 0, swing: 0 };
const CAM_AHEAD = CAM.ahead;

/** The disco's colours, and the beat its lights keep (per second). */
const DISCO: Rgb[] = [[1, 0.2, 0.6], [0.2, 0.9, 1], [1, 0.9, 0.2], [0.55, 0.3, 1], [0.2, 1, 0.4], [1, 0.45, 0.1]];
const LASERS: Rgb[] = [[0.2, 1, 0.3], [1, 0.15, 0.2], [0.25, 0.45, 1], [1, 0.2, 1]];
const BEAT = 124 / 60;
const SIDES = [-1, 1];
/** How long a look back over the shoulder, after overtaking, takes. */
const GLANCE = 0.9;
/** A drift's sparks by tier: none yet (dust instead), then blue, orange, purple — bigger and more of them each tier, too. */
const SPARKS: Rgb[] = [[0, 0, 0], [0.35, 0.65, 1], [1, 0.55, 0.12], [0.85, 0.35, 1]];

/** Where a driver's head sits in the kart, for anyone without a roster body. */
const HEAD_X = -0.34;
const WHITE3 = new Float32Array([1, 1, 1]);
/** For the plain head drawn before a character's model is in. */
const SKIN_TINT = new Float32Array([0.96, 0.79, 0.64, 1]);
const HAIR_TINT = new Float32Array([0.3, 0.2, 0.13, 1]);

/** How far the driver's head is turned over the side, 0…1, with `left` seconds of being sick to go. */
const barfPose = (left: number) => (left > 0 ? Math.min(1, (BARF_TIME - left) / 0.2, left / 0.2) : 0);
const HEAD_Y = 1.8;
/** A trolley's size against a kart's: the moose is a size up on a kart (1.2 × its model), and the trolley's model is smaller than a kart's, so this brings it level with the moose. */
const CART_SCALE = 1.9;
/** The driver in it: only a little bigger than in a kart, and how far up he's lifted to sit in the basket. */
const CART_DRIVER = 1.25;
const CART_SEAT = 0.55;

const CELL = 128;
/** The atlas's last row is the chalet's sign: u0, v0, u1, v1. */
const SIGN_UV = [0.01, 897 / 1024, 0.99, 1023 / 1024];
/** The row above it, the game's name for the arch over the start line. */
const LOGO_UV = [0.01, 770 / 1024, 0.99, 894 / 1024];

/** The texture the world draws with: nothing but the chalet's painted sign. */
function signAtlas() {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { canvas };
  // A painted board, cream letters on dark wood.
  ctx.fillStyle = "#4a2c17";
  ctx.fillRect(0, 1024 - CELL, 1024, CELL);
  ctx.strokeStyle = "#e9d7b0";
  ctx.lineWidth = 6;
  ctx.strokeRect(14, 1024 - CELL + 10, 996, CELL - 20);
  ctx.fillStyle = "#f4e6c4";
  ctx.font = `bold ${CELL * 0.72}px Georgia,"Times New Roman",serif`;
  // Stretched a little wide, to fill the board, centred on it.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.setTransform(1.35, 0, 0, 1, 512, 1024 - CELL / 2 + 4);
  ctx.fillText("MALAYSIA", 0, 0);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  // The game's name for the arch over the line: chunky yellow on navy, chequered ends.
  const top = 1024 - 2 * CELL;
  ctx.fillStyle = "#1a2457";
  ctx.fillRect(0, top, 1024, CELL);
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 2; col++) {
      ctx.fillStyle = (row + col) % 2 ? "#ffffff" : "#15151a";
      for (const x of [0, 1024 - 64]) ctx.fillRect(x + col * 32, top + row * 16, 32, 16);
    }
  }
  ctx.font = `italic 800 ${CELL * 0.8}px "Bricolage Grotesque",Archivo,Inter,sans-serif`;
  ctx.lineJoin = "round";
  ctx.lineWidth = 14;
  ctx.strokeStyle = "#15151a";
  ctx.fillStyle = "#ffd23a";
  // Stretched wide, as on a banner.
  ctx.setTransform(1.5, 0, 0, 1, 512, top + CELL / 2 + 6);
  ctx.strokeText("DSS KART", 0, 0);
  ctx.fillText("DSS KART", 0, 0);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  paintCloud(ctx, 0, [[110, 170, 62], [190, 120, 82], [290, 135, 74], [370, 175, 58], [240, 185, 60], [160, 190, 48]]);
  paintCloud(ctx, 512, [[90, 180, 50], [160, 140, 66], [245, 110, 78], [330, 145, 64], [410, 180, 46], [250, 190, 52]]);
  return { canvas };
}

/** The atlas's top quarter: two clouds, side by side, each u0, v0, u1, v1 — inset, as the texture wraps and the sign is on the far side of its top edge. */
const CLOUD_UV = [[0.01, 0.01, 0.49, 0.25], [0.51, 0.01, 0.99, 0.25]];

/** A painted cloud into the atlas: puffs of white over a blue-grey underside, in a 512 × 256 cell at x. */
function paintCloud(ctx: CanvasRenderingContext2D, x: number, puffs: number[][]) {
  for (const [shade, dy] of [["#c7dcf2", 14], ["#ffffff", 0]] as const) {
    ctx.fillStyle = shade;
    for (const [px, py, r] of puffs) {
      ctx.beginPath();
      ctx.arc(x + px, py + dy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Round the world, following the camera: the sky — from the horizon's colour
 * (the fog's) up to a deep blue overhead — and two rings of hills in front of
 * it, faded towards the horizon by distance. Unlit, unfogged, one mesh.
 */
export function backdropMesh(theme: Theme) {
  const b = new MeshBuilder();
  const top = theme.skyTop ?? theme.sky;
  const R = 380;
  const [rings, sides] = [10, 32];
  const at = (ring: number) => -0.25 + (ring / rings) * (Math.PI / 2 + 0.25);
  const sky = (el: number): Rgb => {
    const t = Math.pow(Math.max(0, Math.sin(el)), 0.55);
    return [0, 1, 2].map((k) => theme.sky[k] + (top[k] - theme.sky[k]) * t) as Rgb;
  };
  for (let ring = 0; ring < rings; ring++) {
    for (let side = 0; side < sides; side++) {
      const k = b.count;
      for (const [r, sd] of [[ring, side], [ring, side + 1], [ring + 1, side + 1], [ring + 1, side]]) {
        const [el, az] = [at(r), (sd / sides) * Math.PI * 2];
        b.vert([Math.cos(el) * Math.cos(az) * R, Math.sin(el) * R, Math.cos(el) * Math.sin(az) * R], [0, 1, 0], sky(el));
      }
      b.i.push(k, k + 2, k + 1, k, k + 3, k + 2);
    }
  }
  if (theme.hills) {
    // The far ring taller and paler, the near one lower and greener, each
    // its own run of rounded tops.
    const segs = 96;
    [[350, 30, 70, 1], [310, 10, 34, 0]].forEach(([r, lo, hi, far], ring) => {
      const col = theme.hills![far];
      const foot = [0, 1, 2].map((k) => col[k] + (theme.sky[k] - col[k]) * 0.55) as Rgb;
      for (let k = 0; k < segs; k++) {
        const h = (a: number) => lo + (hi - lo) * (0.5 + 0.28 * Math.sin(3 * a + ring * 2) + 0.14 * Math.sin(7 * a + ring) + 0.08 * Math.sin(17 * a + ring * 5));
        const [a0, a1] = [(k / segs) * Math.PI * 2, ((k + 1) / segs) * Math.PI * 2];
        const P = (a: number, y: number) => [Math.cos(a) * r, y, Math.sin(a) * r];
        const v = b.vert(P(a0, -20), [0, 1, 0], foot);
        b.vert(P(a1, -20), [0, 1, 0], foot);
        b.vert(P(a1, h(a1)), [0, 1, 0], col);
        b.vert(P(a0, h(a0)), [0, 1, 0], col);
        b.i.push(v, v + 1, v + 2, v, v + 2, v + 3);
      }
    });
  }
  return b;
}

/** Clouds on a ring round the camera, each facing it, from the atlas's two. */
function cloudMesh() {
  const b = new MeshBuilder();
  for (let k = 0; k < 14; k++) {
    const a = k * 0.449 + Math.sin(k * 3.1) * 0.2;
    // Inside the sky dome (380) even at a big cloud's top corners.
    const r = 280 + 15 * Math.sin(k * 1.7);
    const [w, y] = [60 + 40 * ((k * 0.618) % 1), 60 + 50 * ((k * 0.381) % 1)];
    const [cx, cz] = [Math.cos(a) * r, Math.sin(a) * r];
    const [dx, dz] = [-Math.sin(a) * (w / 2), Math.cos(a) * (w / 2)];
    const [u0, v0, u1, v1] = CLOUD_UV[k % 2];
    b.quad([cx - dx, y, cz - dz], [cx + dx, y, cz + dz], [cx + dx, y + w / 2, cz + dz], [cx - dx, y + w / 2, cz - dz], [-Math.cos(a), 0, -Math.sin(a)], [1, 1, 1], [
      [u0, v1], [u1, v1], [u1, v0], [u0, v0],
    ]);
  }
  return b;
}

// ── Matrices, without allocating ─────────────────────────────────────────────

/**
 * out = T(x, y, z) · Ry(yaw) · Rz(pitch) · Rx(roll) · S(sx, sy, sz), written
 * straight into `out` — the one transform every object here needs, worked
 * out once instead of multiplied together from temporaries every frame.
 */
export function place(
  out: Float32Array,
  x: number, y: number, z: number,
  yaw = 0, pitch = 0, roll = 0,
  sx = 1, sy = sx, sz = sx,
) {
  const cy = Math.cos(yaw), sy_ = Math.sin(yaw);
  const cz = Math.cos(pitch), sz_ = Math.sin(pitch);
  const cx = Math.cos(roll), sx_ = Math.sin(roll);
  // R = Ry · Rz · Rx, by rows.
  const r00 = cy * cz, r01 = -cy * sz_ * cx + sy_ * sx_, r02 = cy * sz_ * sx_ + sy_ * cx;
  const r10 = sz_, r11 = cz * cx, r12 = -cz * sx_;
  const r20 = -sy_ * cz, r21 = sy_ * sz_ * cx + cy * sx_, r22 = -sy_ * sz_ * sx_ + cy * cx;
  out[0] = r00 * sx; out[1] = r10 * sx; out[2] = r20 * sx; out[3] = 0;
  out[4] = r01 * sy; out[5] = r11 * sy; out[6] = r21 * sy; out[7] = 0;
  out[8] = r02 * sz; out[9] = r12 * sz; out[10] = r22 * sz; out[11] = 0;
  out[12] = x; out[13] = y; out[14] = z; out[15] = 1;
  return out;
}

/** One vertex of a dynamic mesh (MeshBuilder's layout) into `d` at `o`, facing up; returns the next `o`. */
function putVertex(d: Float32Array, o: number, x: number, y: number, z: number, r: number, g: number, b: number, u: number, v: number) {
  d[o] = x; d[o + 1] = y; d[o + 2] = z;
  d[o + 3] = 0; d[o + 4] = 1; d[o + 5] = 0;
  d[o + 6] = r; d[o + 7] = g; d[o + 8] = b;
  d[o + 9] = u; d[o + 10] = v;
  return o + FLOATS_PER_VERTEX;
}

/** A point through a matrix, into `out`. */
function transform(out: Float32Array, m: Float32Array, x: number, y: number, z: number) {
  out[0] = m[0] * x + m[4] * y + m[8] * z + m[12];
  out[1] = m[1] * x + m[5] * y + m[9] * z + m[13];
  out[2] = m[2] * x + m[6] * y + m[10] * z + m[14];
  return out;
}

function perspectiveGl(out: Float32Array, fovY: number, aspect: number, near: number, far: number) {
  const f = 1 / Math.tan(fovY / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) / (near - far);
  out[11] = -1;
  out[14] = (2 * far * near) / (near - far);
  return out;
}

/** A camera at `eye` looking at `target`, world up — rolled `roll` radians about its view. */
function lookAtInto(out: Float32Array, ex: number, ey: number, ez: number, tx: number, ty: number, tz: number, roll = 0) {
  let zx = ex - tx, zy = ey - ty, zz = ez - tz;
  const zl = Math.hypot(zx, zy, zz) || 1;
  zx /= zl; zy /= zl; zz /= zl;
  // x = up × z, with up = (0, 1, 0).
  let xx = zz, xz = -zx;
  const xl = Math.hypot(xx, xz) || 1;
  xx /= xl; xz /= xl;
  let yx = zy * xz, yy = zz * xx - zx * xz, yz = -zy * xx;
  let xy = 0;
  if (roll) {
    const c = Math.cos(roll);
    const r = Math.sin(roll);
    const x0 = xx;
    const z0 = xz;
    xx = x0 * c + yx * r;
    xy = yy * r;
    xz = z0 * c + yz * r;
    yx = yx * c - x0 * r;
    yy *= c;
    yz = yz * c - z0 * r;
  }
  out[0] = xx; out[1] = yx; out[2] = zx; out[3] = 0;
  out[4] = xy; out[5] = yy; out[6] = zy; out[7] = 0;
  out[8] = xz; out[9] = yz; out[10] = zz; out[11] = 0;
  out[12] = -(xx * ex + xy * ey + xz * ez);
  out[13] = -(yx * ex + yy * ey + yz * ez);
  out[14] = -(zx * ex + zy * ey + zz * ez);
  out[15] = 1;
  return out;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpAngle = (a: number, b: number, t: number) => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;

// ── The renderer ─────────────────────────────────────────────────────────────

/**
 * The renderer for a canvas: WebGPU if the device will give us one, WebGL if
 * not, or null if neither — the caller says so on screen.
 */
export async function createKartRenderer(canvas: HTMLCanvasElement, onlyGl = false): Promise<KartRenderer | null> {
  const gpu = onlyGl ? null : await createGpuBackend(canvas).catch(() => null);
  const backend = gpu ?? createGlBackend(canvas);
  return backend && buildRenderer(canvas, backend);
}

function buildRenderer(canvas: HTMLCanvasElement, first: Backend): KartRenderer {
  let backend = first;
  let destroyed = false;
  let recovering = false;

  // Everything that lives on the GPU, rebuilt whenever the backend is.
  let gpu = makeResources(backend);
  let scene: ReturnType<typeof makeScene> | null = null;
  let current: Race | null = null;
  // Loaded models, kept across races: the files don't change between them.
  let models = new Map<string, Mesh>();
  /** A model's parts — a head's faces, a body's poses — as index ranges into it, and the views drawn from them. */
  const ranges = new Map<string, Record<string, number[]>>();
  let parts = new Map<string, Map<string, Mesh>>();
  /** Models just uploaded, drawn once at zero size next frame so the GPU has them ready before they're first seen. */
  const fresh: Mesh[] = [];

  const want = (name: string) => {
    if (models.has(name)) return;
    const forBackend = backend;
    loadModel(name).then((data) => {
      if (data && !destroyed && backend === forBackend && !models.has(name)) {
        const mesh = uploadModel(backend, data);
        models.set(name, mesh);
        fresh.push(mesh);
        if (data.ranges) ranges.set(name, data.ranges);
      }
    });
  };
  /** One part of a loaded model — `char-joa` `joy`, `body-M` `cheer` — or undefined until it's in. */
  const part = (name: string | null, label: string) => {
    if (!name) return undefined;
    // By name, then label: no key strings built, as this runs a few times per kart per frame.
    let byLabel = parts.get(name);
    if (!byLabel) parts.set(name, (byLabel = new Map()));
    let view = byLabel.get(label);
    const whole = models.get(name);
    const r = ranges.get(name)?.[label];
    if (!view && whole && r) byLabel.set(label, (view = { ...whole, first: r[0], count: r[1] }));
    return view;
  };

  function makeScene(race: Race) {
    const { world, road, line, pads, props, details, water, terrain, sign, forest, trim, statue } = buildWorld(race.track, isDebugOn());
    const atlas = signAtlas();
    const styles = fieldStyles(race.karts);
    backend.texture(atlas.canvas);
    // The arch's lettering is in the HUD's font: painted again once it's in, if it wasn't yet.
    const font = 'italic 800 80px "Bricolage Grotesque"';
    if (typeof document !== "undefined" && document.fonts && !document.fonts.check(font)) {
      const forBackend = backend;
      document.fonts.load(font).then(() => !destroyed && backend === forBackend && current === race && backend.texture(signAtlas().canvas)).catch(() => {});
    }
    const upload = (b: MeshBuilder) => backend.mesh(new Float32Array(b.v), new Uint16Array(b.i), false);
    const f32 = (c: number[]) => new Float32Array(c);
    return {
      world: upload(world),
      road: upload(road),
      line: upload(line),
      pads: upload(pads),
      props: upload(props),
      details: upload(details),
      water: upload(water),
      terrain: upload(terrain),
      sign: upload(sign),
      forest: forest.map(upload),
      trim: upload(trim),
      /** Where the giant moose stands, and which way it faces: x, z, yaw. */
      statue,
      /** The sky and hills, and the clouds: the new look only. */
      backdrop: LOOK === "new" ? upload(backdropMesh(race.track.map.theme)) : null,
      clouds: LOOK === "new" ? upload(cloudMesh()) : null,
      /** How high the ground is under the DJ's stage. */
      djY: race.track.map.dj ? terrainY(race.track, ...race.track.map.dj) : 0,
      karts: new Map(
        race.karts.map((k) => {
          const style = styles.get(k.id)!;
          const c = style.char && ROSTER[style.char];
          // The character's head (faces and gear in it), the body type it sits on, and its kart.
          const head = c ? `char-${style.char}` : null;
          const body = c ? `body-${c.body}` : null;
          [`kart-${style.kind}`, head, body].forEach((name) => name && want(name));
          // Where the head sits on that body, and the neck it turns about.
          const [hx, hy] = c ? BODIES[c.body as keyof typeof BODIES].head : [HEAD_X, HEAD_Y];
          // Suit and kart in the character's colours; skin and hair are built into the head.
          const paint: Paint = { body: f32(style.body), accent: f32(style.accent), skin: WHITE3, hair: WHITE3 };
          // `slide`: how far round the kart is drawn from the way it's going, easing in and out of a drift;
          // `place` and `glance`: its place last frame, and seconds left of a look back after overtaking.
          // `tier`, `flash`: its drift tier last frame, and what's left of the flash as it went up one;
          // `spun`, `burst`: whether it was twirling last frame, and what's left of the star burst as it started.
          return [k.id, { box: upload(buildKart(k)), style, paint, head, body, hx, hy, neck: hy - 0.42, slide: 0, place: k.place, glance: 0, tier: 0, flash: 0, spun: false, burst: 0, tint: new Float32Array([1, 1, 1, 1]), kartModel: `kart-${style.kind}` }];
        }),
      ),
      freeAll() {
        [this.world, this.road, this.line, this.pads, this.props, this.details, this.water, this.terrain, this.sign, this.trim, ...this.forest, ...Array.from(this.karts.values(), (k) => k.box)].forEach((m) =>
          backend.free(m),
        );
        if (this.backdrop) backend.free(this.backdrop);
        if (this.clouds) backend.free(this.clouds);
      },
    };
  }

  // The camera's shake, and how far its view is widened for a boost — neither
  // for anyone who's asked for less motion.
  const calm = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  let shake = 0;
  let widen = 0;
  /** The camera's roll into a turn (radians), and how far it's swung out sideways in a drift. */
  let tilt = 0;
  let swing = 0;
  let camHeading = 0;
  let camX = 0;
  let camZ = 0;
  /** How high the road is under the camera's kart, and where it's looking. */
  let camY = 0;
  let lookY = 0;
  let width = 1;
  let height = 1;
  let pixels = [1, 1];

  let ghost: number[] | null = null;
  const ghostTint = new Float32Array([0.75, 0.9, 1, 0.35]);

  const setRace = (race: Race) => {
    scene?.freeAll();
    current = race;
    scene = makeScene(race);
    ITEM_MODELS.forEach(want);
    const player = race.karts.find((k) => k.player) ?? race.karts[0];
    camHeading = player.heading;
    camX = player.x;
    camZ = player.z;
    camY = lookY = race.track.samples[player.idx].y;
  };

  // WebGPU can lose its device — the phone reclaims the GPU while the app is
  // in the background, say. Start again on a fresh one, race and all.
  const recover = async () => {
    recovering = true;
    const next = await createGpuBackend(canvas).catch(() => null);
    recovering = false;
    if (destroyed || !next) return;
    backend = next;
    gpu = makeResources(backend);
    models = new Map();
    parts = new Map();
    backend.resize(pixels[0], pixels[1]);
    if (current) setRace(current);
  };

  // The frame, and everything it's built in — allocated once.
  const frame: Frame = {
    viewProj: new Float32Array(16),
    eye: new Float32Array(3),
    fog: [1, 1, 1],
    time: 0,
    look: LOOK === "new" ? 1 : 0,
    light: new Float32Array([0, 1, 0, 0, ...HEMISPHERE.sky, 0, ...HEMISPHERE.ground, 0]),
    draws: [],
    count: 0,
  };
  const proj = new Float32Array(16);
  const view = new Float32Array(16);
  const placed = new Float32Array(16);
  const seat = new Float32Array(16);
  const local = new Float32Array(16);
  const base = new Float32Array(16);
  const point = new Float32Array(3);
  const mouth = new Float32Array(3);
  /** The head's turn about the neck: a little into a corner, or right round and over the side, being sick. */
  /** …and `look` further round, `nod` up and down: a glance behind, a dizzy shake, a cheer. */
  const headPose = (out: Float32Array, at: { hx: number; neck: number }, steer: number, sick: number, look = 0, nod = 0) => {
    place(out, 0, 0, 0, -steer * 0.35 * (1 - sick) - 1.25 * sick + look, -0.45 * sick + nod, 0.55 * sick);
    transform(neck, out, at.hx, at.neck, 0);
    out[12] = at.hx - neck[0];
    out[13] = at.neck - neck[1];
    out[14] = -neck[2];
    return out;
  };
  const neck = new Float32Array(3);
  const right = new Float32Array(3);
  const up = new Float32Array(3);
  const WHITE4 = new Float32Array([1, 1, 1, 1]);
  /** No paint to put on, but the plastic sheen of a model all the same. */
  const PLAIN: Paint = { body: WHITE3, accent: WHITE3, skin: WHITE3, hair: WHITE3 };
  const flameTint = new Float32Array([1, 1, 1, 0.9]);
  const cubeTint = new Float32Array([1, 1, 1, 0.55]);
  const waterTint = new Float32Array([1, 1, 1, 0.62]);
  const sprayTints = [new Float32Array([0.8, 0.9, 1, 0.6]), new Float32Array([1, 1, 1, 0.9])];
  const shadowTint = new Float32Array([1, 1, 1, LOOK === "new" ? 0.45 : 0.32]);
  const smokeTint = new Float32Array([1, 1, 1, 0.6]);
  const boxSpin: Float32Array[] = [];

  /** The next pooled draw command, or null once the frame is full. */
  const push = (mesh: Mesh, mode: number, blend: Blend, tint = WHITE4, paint: Paint | null = null) => {
    if (!mesh.count || frame.count >= MAX_DRAWS) return null;
    let d = frame.draws[frame.count];
    if (!d) d = frame.draws[frame.count] = { mesh, mode, blend, model: new Float32Array(16), tint, paint };
    d.mesh = mesh;
    d.mode = mode;
    d.blend = blend;
    d.tint = tint;
    d.paint = paint;
    frame.count++;
    return d;
  };
  const IDENTITY = place(new Float32Array(16), 0, 0, 0);

  /** A camera-facing square into a dynamic mesh: embers, puffs. */
  const billboard = (
    m: DynamicMesh, x: number, y: number, z: number, size: number,
    r: number, g: number, b: number, u0: number, v0: number, u1: number, v1: number,
  ) => {
    if (m.quads >= m.maxQuads) return;
    // How much of the screen it covers, for the overdraw readout.
    const d2 = (x - frame.eye[0]) ** 2 + (y - frame.eye[1]) ** 2 + (z - frame.eye[2]) ** 2;
    stats.overdraw += Math.min(1, (size * size * cover) / d2);
    // No closures or arrays here: this runs a couple of thousand times a frame.
    const h = size / 2;
    const Rx = right[0] * h, Ry = right[1] * h, Rz = right[2] * h;
    const Ux = up[0] * h, Uy = up[1] * h, Uz = up[2] * h;
    const d = m.data;
    let o = m.quads * 4 * FLOATS_PER_VERTEX;
    o = putVertex(d, o, x - Rx - Ux, y - Ry - Uy, z - Rz - Uz, r, g, b, u0, v1);
    o = putVertex(d, o, x + Rx - Ux, y + Ry - Uy, z + Rz - Uz, r, g, b, u1, v1);
    o = putVertex(d, o, x + Rx + Ux, y + Ry + Uy, z + Rz + Uz, r, g, b, u1, v0);
    putVertex(d, o, x - Rx + Ux, y - Ry + Uy, z - Rz + Uz, r, g, b, u0, v0);
    m.quads++;
  };
  const addEmber = (x: number, y: number, z: number, size: number, r: number, g: number, b: number) =>
    billboard(gpu.embers, x, y, z, size, r, g, b, 0, 1, 1, 0);
  /** Smoke and dust: shrunk away to nothing as they near the camera, so a cloud never covers the screen. */
  const addPuff = (x: number, y: number, z: number, size: number, r: number, g = r, b = r) => {
    const near = Math.hypot(x - frame.eye[0], y - frame.eye[1], z - frame.eye[2]);
    if (LOOK === "new") size *= Math.min(1, Math.max(0, (near - 4) / 8));
    if (size > 0.02) billboard(gpu.smoke, x, y, z, size, r, g, b, 0, 0, 1, 1);
  };
  const addShadow = (x: number, y: number, z: number, radius: number) => {
    const m = gpu.shadows;
    if (m.quads >= m.maxQuads) return;
    const d = m.data;
    const h = y + 0.06;
    let o = m.quads * 4 * FLOATS_PER_VERTEX;
    o = putVertex(d, o, x - radius, h, z - radius, 0, 0, 0, 0, 0);
    o = putVertex(d, o, x + radius, h, z - radius, 0, 0, 0, 1, 0);
    o = putVertex(d, o, x + radius, h, z + radius, 0, 0, 0, 1, 1);
    putVertex(d, o, x - radius, h, z + radius, 0, 0, 0, 0, 1);
    m.quads++;
  };

  const tints = { tile: [] as Float32Array[], laser: [] as Float32Array[], spot: [] as Float32Array[] };
  const tintOf = (pool: Float32Array[], k: number, c: Rgb, bright: number, a = 1) => {
    const t = (pool[k] ??= new Float32Array(4));
    t[0] = c[0] * bright;
    t[1] = c[1] * bright;
    t[2] = c[2] * bright;
    t[3] = a;
    return t;
  };

  /** What moves beside and across the road: moose, croquet, mosquitoes, the campfire, the disco. */
  const scenery = (race: Race, time: number) => {
    const { track } = race;
    const { map } = track;
    const moose = models.get("item-moose");
    const swarm = models.get("item-mosquito");
    const stack = models.get("item-soundboks");
    for (const h of track.hazards) {
      const s = track.samples[h.idx];
      const lat = hazardLat(h, time);
      const x = s.x + s.nx * lat;
      const z = s.z + s.nz * lat;
      if (h.kind === "moose") {
        // Ambling across, head bobbing; waiting at the side, facing the road.
        const moving = hazardLat(h, time + 0.1) - lat;
        const dir = moving ? Math.sign(moving) : -Math.sign(lat);
        const stride = moving ? time * 6 + h.idx : 0;
        addShadow(x, s.y, z, 2.2);
        if (map.alpine) {
          const d = push(gpu.cow, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, x, s.y + Math.abs(Math.cos(stride)) * 0.1, z, -Math.atan2(s.nz * dir, s.nx * dir), Math.sin(stride) * 0.04, 0, 1.7);
        } else if (moose) {
          const d = push(moose, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, x, s.y + Math.abs(Math.cos(stride)) * 0.15, z, -Math.atan2(s.nz * dir, s.nx * dir), Math.sin(stride) * 0.05, 0, 1.5);
        }
      } else if (h.kind === "ball") {
        // The ball rolls across; a giant mallet either side swings back,
        // whacks it over the road, and follows through. In the Alps, a
        // snowball rolls down across it on its own.
        addShadow(x, s.y, z, 1.5);
        const yaw = -Math.atan2(s.nz, s.nx);
        const ball = push(map.alpine ? gpu.snowball : gpu.balls[h.idx % gpu.balls.length], 0, "opaque", WHITE4, PLAIN);
        if (ball) place(ball.model, x, s.y + BALL_RADIUS, z, yaw, -lat / BALL_RADIUS);
        if (map.alpine) continue;
        const [walk, wait] = CROSSING.ball;
        const cycle = 2 * (walk + wait);
        const p = crossingPhase(h, time);
        for (const side of SIDES) {
          const q = (p - (side < 0 ? 0 : walk + wait) + cycle) % cycle;
          const swing =
            q < 0.15 ? (0.5 * q) / 0.15
            : q < 1 ? 0.5 * (1 - (q - 0.15) / 0.85)
            : q > cycle - 0.15 ? (-1.1 * (cycle - q)) / 0.15
            : q > cycle - 0.9 ? (-1.1 * (q - cycle + 0.9)) / 0.75
            : 0;
          const ml = side * (CROSSING_SPAN + BALL_RADIUS + 1.15);
          const d = push(gpu.mallet, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, s.x + s.nx * ml, s.y + 4.2, s.z + s.nz * ml, side < 0 ? yaw : yaw + Math.PI, swing);
        }
      } else if (h.kind === "swarm") {
        // A cloud of mosquitoes, darting about over the road.
        addShadow(x, s.y, z, SWARM_RADIUS);
        for (let k = 0; k < 14; k++) {
          const a = time * (2 + (k % 5) * 0.7) * (k % 2 ? 1 : -1) + k * 2.4;
          const r = SWARM_RADIUS * (0.25 + 0.65 * ((k * 0.618) % 1));
          const jx = Math.sin(time * 23 + k) * 0.15;
          // Each flying round its own circle, nose along it, wings a blur.
          const d = push(gpu.mosquito, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, x + Math.cos(a) * r + jx, s.y + 1.7 + Math.sin(time * 3.1 + k * 1.3) * 0.9, z + Math.sin(a) * r, (k % 2 ? 1 : -1) * a - Math.PI / 2 * (k % 2 ? 1 : -1), 0, Math.sin(time * 60 + k) * 0.25, 1.4);
        }
        for (let k = 0; k < 2; k++) {
          const d = swarm && push(swarm, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, x + Math.sin(time * 1.7 + k * 3) * 1.4, s.y + 1.5 + k * 0.6, z + Math.cos(time * 1.3 + k * 3) * 1.4, time * (5 + k), 0, 0, 1.6);
        }
      } else if (h.kind === "speaker") {
        // A stack of Soundboks by the road, facing across it, thumping on every
        // beat of the sim's clock: the same ring of bass as on a kart.
        const since = time % BASS_BEAT;
        const pulse = Math.exp(-since * 12);
        const across = h.lat > 0 ? Math.PI : 0;
        const yaw = -Math.atan2(s.nz, s.nx) + across;
        addShadow(x, s.y, z, 2.6);
        for (let k = 0; k < 3; k++) {
          const d = stack && push(stack, 0, "opaque", WHITE4, PLAIN);
          if (d) place(d.model, x, s.y + 1.1 + k * 2.2, z, yaw, 0, 0, 2.4 * (1 + pulse * 0.08 * (k + 1)));
        }
        const wave = since / BASS_BEAT;
        const ring = BASS_RADIUS * Math.sqrt(wave);
        for (let k = 0; k < 48; k++) {
          const a = (k / 48) * Math.PI * 2;
          const glow = 1 - wave;
          addEmber(x + Math.cos(a) * ring, s.y + 0.4, z + Math.sin(a) * ring, 1.2 + glow, glow, glow * 0.35, glow * 0.9);
        }
      }
    }

    if (map.campfire) {
      // Flames licking up out of the logs, sparks rising, and its glow.
      const [x, z] = map.campfire;
      for (let k = 0; k < 4; k++) {
        const len = 4.2 - k * 0.5 + 0.8 * Math.sin(time * 9 + k * 2) + 0.4 * Math.sin(time * 23 + k);
        const w = 6.5 - k * 1.1;
        const d = push(gpu.flame, 5, "add", flameTint);
        if (d) place(d.model, x + Math.cos(k * 1.6) * 0.5, 0.4, z + Math.sin(k * 1.6) * 0.5, k * 1.6 + time * 0.7, Math.PI / 2, 0, len, w, w);
      }
      for (let i = 0; i < 24; i++) {
        const life = (time * 0.5 + i / 24) % 1;
        const fade = 1 - life;
        const drift = Math.sin(i * 7.3 + time * 1.5) * 2 * life;
        addEmber(x + drift, 1.2 + life * 10, z + Math.cos(i * 4.1 + time) * 1.5 * life, 0.18 + 0.35 * fade, fade, fade * 0.45, fade * 0.08);
      }
      addEmber(x, 2, z, 11 + Math.sin(time * 11) * 0.8, 0.45, 0.22, 0.05);
      addEmber(x, 1, z, 5, 0.5, 0.35, 0.1);
    }

    if (map.dj) {
      const [x, z] = map.dj;
      const y = scene!.djY;
      const beat = time * BEAT;
      const pulse = 1 - (beat % 1);
      // The DJ behind the decks, and dancers on the floor, all on the beat.
      // The DJ faces the floor, nodding; the dancers face the DJ.
      const dj = push(gpu.dj, 0, "opaque", WHITE4, PLAIN);
      if (dj) place(dj.model, x, y + 2.2 + Math.abs(Math.sin(beat * Math.PI)) * 0.08, z - 0.6, Math.PI / 2, 0, Math.sin(beat * Math.PI) * 0.06, 1.35);
      for (let k = 0; k < 4; k++) {
        const hop = Math.abs(Math.sin((beat + k * 0.5) * Math.PI)) * 0.5;
        const d = push(gpu.dancers[k], 0, "opaque", WHITE4, PLAIN);
        if (d) place(d.model, x - 4.5 + k * 3, y + 0.05 + hop, z - 8 - (k % 2) * 3, -Math.PI / 2, 0, Math.sin((beat + k * 0.5) * Math.PI) * 0.12, 1.15);
      }
      // The dance floor, a new colour every beat.
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          const c = DISCO[(Math.floor(beat) + i * 2 + j * 3) % DISCO.length];
          const d = push(gpu.tile, 0, "opaque", tintOf(tints.tile, i * 4 + j, c, 0.55 + 0.6 * pulse));
          if (d) place(d.model, x - 3 + i * 2, y + 0.03, z - 6 - j * 2, 0, 0, 0, 1.9);
        }
      }
      // The disco ball, turning, glinting.
      const d = push(gpu.disco, 0, "opaque", WHITE4, PLAIN);
      if (d) place(d.model, x, y + 6, z + 2, time * 1.2, 0, 0, 0.9);
      for (let k = 0; k < 8; k++) {
        const a = k * 0.785 + time * 1.2;
        const glint = Math.max(0, Math.sin(time * 9 + k * 3.7));
        addEmber(x + Math.cos(a) * 0.95, y + 6 + Math.sin(k * 1.9) * 0.6, z + 2 + Math.sin(a) * 0.95, 0.1 + 0.6 * glint, glint, glint, glint);
      }
      // Specks of light thrown off it, sweeping round the floor and the road.
      for (let k = 0; k < 14; k++) {
        const a = time * 0.8 + k * 0.45;
        const r = 4 + (k % 4) * 3;
        const c = DISCO[k % DISCO.length];
        addEmber(x + Math.cos(a) * r, y + 0.4 + (k % 3) * 1.4, z - 7 + Math.sin(a) * r * 0.7, 0.7, c[0] * 0.8, c[1] * 0.8, c[2] * 0.8);
      }
      // Lasers from the booth, fanning out over the floor and across the road.
      const laser = LASERS[Math.floor(beat / 4) % LASERS.length];
      for (let k = 0; k < 6; k++) {
        const c = k % 2 ? laser : LASERS[(Math.floor(beat / 4) + 2) % LASERS.length];
        const b = push(gpu.beam, 7, "add", tintOf(tints.laser, k, c, 1));
        const yaw = Math.PI / 2 + Math.sin(time * 1.3 + k * 0.8) * 0.6;
        if (b) place(b.model, x - 1.5 + k * 0.6, y + 2.4, z - 2.3, yaw, -0.07 + Math.sin(time * 0.9 + k) * 0.04, 0, 42, 0.18, 0.18);
      }
      // Coloured spots off the truss, swinging over the dancers.
      for (let k = 0; k < 5; k++) {
        const cx = x - 6 + k * 3;
        const cy = 8.5;
        const cz = z + 2;
        const tx = x + Math.sin(time * 0.7 + k) * 4 - cx;
        const tz = z - 8 + Math.cos(time * 0.9 + k * 1.3) * 3 - cz;
        const len = Math.hypot(tx, cy, tz);
        const c = DISCO[(k + Math.floor(beat / 2)) % DISCO.length];
        const b = push(gpu.beam, 7, "add", tintOf(tints.spot, k, c, 0.35 + 0.25 * pulse));
        if (b) place(b.model, cx, y + cy, cz, -Math.atan2(tz, tx), -Math.atan2(cy, Math.hypot(tx, tz)), 0, len, 1.8, 1.8);
        addEmber(cx, y + cy - 0.3, cz, 1.3, c[0], c[1], c[2]);
        addEmber(cx + tx, y + 0.3, cz + tz, 3.2, c[0] * 0.4, c[1] * 0.4, c[2] * 0.4);
      }
    }
  };

  const render = (race: Race, alpha: number, dt: number) => {
    if (backend.lost) {
      if (!recovering) recover();
      return;
    }
    if (!scene) return;
    frame.count = 0;
    gpu.shadows.quads = gpu.embers.quads = gpu.smoke.quads = gpu.goo.quads = 0;
    stats.overdraw = 0;
    // The sim is one step ahead of what's on screen; everything is drawn
    // `alpha` of the way from where it was to where it is, so motion is
    // smooth whatever the screen's refresh rate or a frame's hiccup.
    const time = race.time - STEP + alpha * STEP;
    const sky = race.track.map.theme.sky;

    // A low chase camera just behind the kart, looking down the road towards
    // the horizon, turning with the player so left on the screen is always left.
    const player = race.karts.find((k) => k.player) ?? race.karts[0];
    const px = lerp(player.px, player.x, alpha);
    const pz = lerp(player.pz, player.z, alpha);
    const ph = lerpAngle(player.ph, player.heading, alpha);
    // Over the line: the camera swings round to face the driver.
    const around = player.finishTime !== null ? Math.min(Math.PI * 0.85, (raceTime(race) - player.finishTime) * 0.8) : 0;
    const aim = ph + around;
    camHeading += Math.atan2(Math.sin(aim - camHeading), Math.cos(aim - camHeading)) * (1 - Math.exp(-dt * 5));
    camX += (px - camX) * (1 - Math.exp(-dt * 12));
    camZ += (pz - camZ) * (1 - Math.exp(-dt * 12));
    const fx = Math.cos(camHeading);
    const fz = Math.sin(camHeading);
    // Up and down the hills with the road, looking where it goes.
    const { track } = race;
    camY += (roadY(track, player.idx, px, pz) - camY) * (1 - Math.exp(-dt * 8));
    lookY += (track.samples[(player.idx + CAM_AHEAD) % track.n].y - lookY) * (1 - Math.exp(-dt * 8));
    // A jolt that dies away fast, wobbling the camera up and down and side to side.
    shake *= Math.exp(-dt * TUNING.juice.shakeDecay);
    const jx = calm ? 0 : shake * Math.sin(time * 47);
    const jy = calm ? 0 : shake * Math.sin(time * 61 + 1);
    // Rolled a little into a turn, and swung out wide in a drift.
    const steering = lerp(player.ps, player.steer, alpha);
    tilt += ((calm ? 0 : steering * CAM.roll * (Math.PI / 180)) - tilt) * (1 - Math.exp(-dt * 4));
    swing += ((calm ? 0 : -player.drift * CAM.swing) - swing) * (1 - Math.exp(-dt * 3));
    const sx = -fz * (jx + swing);
    const sz = fx * (jx + swing);
    const ex = camX - fx * CAM.back + sx;
    const ey = CAM.height + camY + jy;
    const ez = camZ - fz * CAM.back + sz;
    const tx = camX + fx * CAM.ahead + sx * 0.5;
    const ty = CAM.look + lookY;
    const tz = camZ + fz * CAM.ahead + sz * 0.5;
    const aspect = width / height;
    // On a boost the view widens, the harder the boost the wider, easing in and out.
    const boosting = calm ? 0 : player.frenzy > 0 && player.roll <= 0 ? 1 : player.boost > 0 ? Math.min(1, (player.boostMul - 1) / 0.55) : 0;
    widen += (boosting - widen) * (1 - Math.exp(-dt * 6));
    // The old look: portrait phones are narrow, so the view widens until the road fits across.
    const fov = CAM.fov
      ? (CAM.fov + CAM.boostFov * widen) * (Math.PI / 180)
      : 2 * Math.atan(Math.max(Math.tan(0.52), 0.48 / aspect) * (1 + TUNING.juice.boostFov * widen));
    if (backend.depthZeroToOne) proj.set(perspectiveZeroToOne(fov, aspect, 0.5, 400));
    else perspectiveGl(proj, fov, aspect, 0.5, 400);
    multiply(proj, lookAtInto(view, ex, ey, ez, tx, ty, tz, tilt), frame.viewProj);
    cover = (proj[5] * proj[5]) / (4 * aspect);
    frame.eye[0] = ex;
    frame.eye[1] = ey;
    frame.eye[2] = ez;
    frame.fog = sky;
    frame.time = time;
    // The new look's sun: high, over the camera's right shoulder, so the backs
    // of the karts — what the camera sees — are the lit side.
    const sun = frame.light;
    sun[0] = -fx * 0.6 - fz * 0.45;
    sun[1] = 1;
    sun[2] = -fz * 0.6 + fx * 0.45;
    const sl = Math.hypot(sun[0], sun[1], sun[2]);
    sun[0] /= sl;
    sun[1] /= sl;
    sun[2] /= sl;

    // Billboards face the camera: its right and up, in world space.
    const dx = tx - ex;
    const dy = ty - ey;
    const dz = tz - ez;
    const fl = Math.hypot(dx, dy, dz);
    right[0] = -fz; right[1] = 0; right[2] = fx;
    up[0] = (right[1] * dz - right[2] * dy) / fl;
    up[1] = (right[2] * dx - right[0] * dz) / fl;
    up[2] = (right[0] * dy - right[1] * dx) / fl;

    // The ground and the walls, and a dirt road, grained in the new look; tarmac chequered; the start line on its own.
    const grain = LOOK === "new" ? 9 : 0;
    if (!whole.has(scene.world)) {
      whole.clear();
      [scene.world, scene.road, scene.line, scene.pads, scene.props, scene.details, scene.terrain, scene.sign, scene.trim, scene.water, ...scene.forest,
        gpu.shadows, gpu.embers, gpu.smoke, gpu.goo].forEach((m) => whole.add(m));
      if (scene.backdrop) whole.add(scene.backdrop);
      if (scene.clouds) whole.add(scene.clouds);
    }
    push(scene.world, grain, "opaque")?.model.set(IDENTITY);
    push(scene.road, race.track.map.theme.dirt ? grain : 1, "opaque")?.model.set(IDENTITY);
    push(scene.trim, 0, "opaque")?.model.set(IDENTITY);
    push(scene.line, 1, "opaque")?.model.set(IDENTITY);
    push(scene.pads, 2, "opaque")?.model.set(IDENTITY);
    push(scene.props, 0, "opaque")?.model.set(IDENTITY);
    push(scene.details, 0, "opaque")?.model.set(IDENTITY);
    push(scene.terrain, 0, "opaque")?.model.set(IDENTITY);
    push(scene.sign, 3, "opaque")?.model.set(IDENTITY);
    for (const m of scene.forest) push(m, 0, "opaque")?.model.set(IDENTITY);
    for (const b of track.branches) {
      const s = track.samples[b.idx];
      const d = push(gpu.branch, 0, "opaque");
      if (d) place(d.model, s.x + s.nx * b.lat, s.y, s.z + s.nz * b.lat, Math.atan2(s.nx, s.nz));
    }
    for (const man of race.snowmen) {
      // Knocked over: it topples, and is gone until it's been built again.
      const down = SNOWMAN_GONE - man.gone;
      if (man.gone > 0 && down > 0.5) continue;
      const s = track.samples[man.idx];
      const fall = man.gone > 0 ? down * 3.2 : 0;
      addShadow(man.x, man.y, man.z, man.r + 0.2);
      const d = push(man.kubb === "king" ? gpu.king : man.kubb ? gpu.kubb : gpu.snowman, 0, "opaque");
      if (d) place(d.model, man.x, man.y - fall * 0.4, man.z, -Math.atan2(-s.tz, -s.tx), fall, 0);
    }
    for (const duck of race.ducks) {
      const at = duckAt(race, duck);
      if (duck.skier) {
        // Skating across, swaying stride to stride; run into and they go
        // sprawling, sliding on across the snow, then gone a while.
        const down = DUCK_GONE - duck.gone;
        if (duck.gone > 0 && down > 2) continue;
        const sway = duck.gone > 0 ? Math.min(1.45, down * 5) : Math.sin(time * 5 + duck.phase) * 0.12;
        addShadow(at.x, at.y, at.z, 0.9);
        const d = push(Math.floor(duck.idx / 18) % 2 ? gpu.skier2 : gpu.skier, 0, "opaque");
        if (d) place(d.model, at.x, at.y, at.z, -Math.atan2(at.dz, at.dx), 0, sway);
        continue;
      }
      // Flown off: up and away, wings going, then gone until it's back.
      const flown = DUCK_GONE - duck.gone;
      const flying = duck.gone > 0;
      if (flying && flown > 1.5) continue;
      const size = duck.chick ? 0.95 : 1.6;
      const heading = Math.atan2(at.dz, at.dx);
      const stride = time * 9 + duck.lag * 4;
      const away = flying ? flown * 5 : 0;
      const x = at.x + at.dx * away;
      const z = at.z + at.dz * away;
      const y = at.y + (flying ? flown * (2 + flown * 3) : Math.abs(Math.sin(stride)) * 0.06);
      if (!flying) addShadow(x, at.y, z, 0.6 * size);
      // A waddle: rocking side to side with each step.
      place(base, x, y, z, -heading, flying ? 0.35 : 0, flying ? 0 : Math.sin(stride) * 0.12, size);
      push(duck.chick ? gpu.chick : gpu.duck, 0, "opaque")?.model.set(base);
      const flap = flying ? 0.2 + 0.9 * Math.sin(time * 28 + duck.lag) : 0.08;
      for (const side of SIDES) {
        const d = push(duck.chick ? gpu.chickWing : gpu.duckWing, 0, "opaque");
        if (d) multiply(base, place(local, 0, 0.55, side * 0.27, 0, 0, -side * flap, 1, 1, side), d.model);
      }
    }

    const moose = models.get("item-moose");
    const star = models.get("item-star");
    if (scene.statue && moose) {
      // The giant moose on its plinth.
      const d = push(moose, 0, "opaque", WHITE4, PLAIN);
      if (d) place(d.model, scene.statue[0], 3.3, scene.statue[1], scene.statue[2], 0, 0, 6);
    }
    const swarm = models.get("item-mosquito");
    const fogger = models.get("item-smoke");
    const speaker = models.get("item-soundboks");
    const trolley = models.get("item-cart");
    /** A plain head — skin, hair, two eyes — at (x, y) in the kart's own axes, for a driver nobody's modelled as. */
    const plainHead = (x: number, y: number, scale: number) => {
      const skin = push(gpu.headSkin, 0, "opaque", SKIN_TINT, PLAIN);
      if (skin) multiply(placed, place(local, x, y, 0, 0, 0, 0, scale * 0.55), skin.model);
      const hair = push(gpu.headHair, 0, "opaque", HAIR_TINT, PLAIN);
      if (hair) multiply(placed, place(local, x, y, 0, 0, 0, 0, scale * 0.55), hair.model);
    };
    for (const kart of race.karts) {
      const entry = scene.karts.get(kart.id)!;
      const kx = lerp(kart.px, kart.x, alpha);
      const kz = lerp(kart.pz, kart.z, alpha);
      const heading = lerpAngle(kart.ph, kart.heading, alpha);
      const spin = kart.spin > 0 ? ((TUNING.spin.time - kart.spin) / TUNING.spin.time) * Math.PI * 4 : 0;
      // Drifting: turned into the corner, the tail hung out.
      // Your own drift really slides, so the nose only needs a little extra; a computer driver's is all for show.
      entry.slide += (kart.drift * (kart.player && !race.autopilot ? 0.12 : 0.4) - entry.slide) * (1 - Math.exp(-dt * 10));
      const bob = kart.speed > 1 ? Math.sin(time * 30 + kart.id) * 0.03 : 0;
      // Roll into the turn a touch.
      const steer = lerp(kart.ps, kart.steer, alpha);
      // A trick: a barrel roll, all the way round, over the first moment of it.
      const lean = steer * 0.08 * Math.min(1, kart.speed / 10) + (kart.tricked > 0 ? Math.min(1, kart.tricked / 0.4) * Math.PI * 2 : 0);
      // Off a branch: up in the air, nose up on the way up and down on the way down.
      const air = lerp(kart.py, kart.y, alpha);
      // On a hill, nose up or down with the road.
      const ground = roadY(track, kart.idx, kx, kz);
      const ahead = track.samples[(kart.idx + 1) % track.n];
      const behind = track.samples[(kart.idx - 1 + track.n) % track.n];
      const tilt = Math.atan(((ahead.y - behind.y) / 2) * (Math.cos(heading) * ahead.tx + Math.sin(heading) * ahead.tz));
      // Thrown up by a beat of someone's bass, and bounced back down.
      const hop = kart.jolt > 0 ? Math.sin((1 - kart.jolt / JOLT_TIME) * Math.PI) * 0.9 : 0;
      // Just over the line: little hops of joy, a few seconds of them.
      const cheering = kart.finishTime !== null && raceTime(race) - kart.finishTime < 3;
      const joy = cheering ? Math.abs(Math.sin(time * 9)) * 0.35 : 0;
      const lift = ground + bob + air + hop + joy;
      // Overtook someone: a glance back over the shoulder at them, and round again.
      if (kart.place < entry.place) entry.glance = GLANCE;
      entry.place = kart.place;
      entry.glance = Math.max(0, entry.glance - dt);
      const look =
        (entry.glance > 0 ? 2.3 * Math.sin(Math.PI * (1 - entry.glance / GLANCE)) : 0) +
        // Twirling: a dizzy shake of the head.
        (kart.spin > 0 ? Math.sin(time * 22) * 0.6 : 0);
      const nod = cheering ? Math.sin(time * 14) * 0.35 : 0;
      // A shopping trolley in place of the kart — flickering back to the kart
      // in its last second and a half — drawn as big as the moose, driver and all.
      const cart = kart.cart > 0 && trolley && (kart.cart > 1.5 || Math.sin(time * 30) > 0);
      const size = cart ? CART_SCALE : 1;
      const yaw = -heading - spin - entry.slide;
      const pitch = tilt + hop * 0.3 + (air > 0 ? Math.max(-0.3, Math.min(0.3, kart.vy * 0.05)) : 0);
      place(placed, kx, lift, kz, yaw, pitch, lean, size);
      // The driver rides in the bigger trolley at not much more than his own size, lifted to sit in the basket.
      const seated = cart ? place(seat, kx, lift + CART_SEAT, kz, yaw, pitch, lean, CART_DRIVER) : placed;
      addShadow(kx, ground, kz, (1.7 - Math.min(0.6, air * 0.4)) * size);
      const hx = Math.cos(heading);
      const hz = Math.sin(heading);
      const body = cart ? trolley : models.get(entry.kartModel);
      const torso = part(entry.body, "torso");
      const head = part(entry.head, "head");
      const modelled = body && torso && head;
      // Just back from a twirl, and safe from another: flickering.
      const blink = kart.spin <= 0 && kart.safe > 0 && Math.sin(time * 40) > 0;
      if (kart.item && kart.roll <= 0 && isPuddleItem(kart.item) && kart.moose <= 0) {
        // The beer, sick or sauce held ready trails behind, as a shield.
        const held = models.get(itemModel(kart.item));
        const d = held && push(held, 0, "opaque", WHITE4, PLAIN);
        if (d) place(d.model, kx - hx * 2.1, lift + 0.15 + Math.sin(time * 6 + kart.id) * 0.06, kz - hz * 2.1, time * 1.5 + kart.id, 0, 0, 1.1);
      }
      if (kart.id === race.rival) {
        // The rival's mark, an arrow turning and bobbing over its head.
        const d = push(gpu.arrow, 0, "opaque", WHITE4, PLAIN);
        if (d) place(d.model, kx, lift + 3.6 + Math.sin(time * 4) * 0.15, kz, time * 2.4, Math.PI, 0, 1);
      }

      // A rival right in front of the camera — between it and you — fades
      // away, rather than filling the bottom of the screen.
      const near = kart.player || LOOK === "old" ? 1 : Math.min(1, Math.max(0, (Math.hypot(kx - frame.eye[0], kz - frame.eye[2]) - 6) / 5));
      entry.tint[3] = near;
      // Dithered, not blended (shader mode 10): blended, its face showed through the back of its head.
      const fade = near < 1 ? 10 : 0;
      if (blink || near <= 0) {
        // Flickered out this frame, or right in front of the camera: no kart, no driver.
      } else if (kart.moose > 0) {
        // No kart at all: a moose at full gallop, bounding and rocking — and
        // a size up on a kart, as a moose should be.
        const stride = time * 9 + kart.id;
        if (moose) {
          place(push(moose, 0, "opaque", WHITE4, entry.paint)?.model ?? local, kx, Math.abs(Math.cos(stride)) * 0.25 + ground + air, kz, -heading, Math.sin(stride) * 0.1, 0, 1.2);
        }
      } else if (modelled) {
        // The finished pieces, picked by what the kart is doing: an arm pose
        // on the body, an expression on the head (art-direction.md §6).
        const done = kart.finishTime !== null;
        const podium = kart.place <= 3;
        const pose: Pose = done ? (podium ? "cheer" : "slump") : steer > 0.35 ? "right" : steer < -0.35 ? "left" : "drive";
        const face: FaceState = done
          ? podium ? "joy" : "sad"
          : kart.spin > 0 || kart.barf > 0 ? "dizzy" : kart.drift !== 0 || kart.boost > 0 ? "focus" : "neutral";
        push(body, fade, "opaque", entry.tint, entry.paint)?.model.set(placed);
        push(torso, fade, "opaque", entry.tint, entry.paint)?.model.set(seated);
        const arms = part(entry.body, pose);
        if (arms) push(arms, fade, "opaque", entry.tint, entry.paint)?.model.set(seated);
        const gear = part(entry.head, "gear");
        if (gear) push(gear, fade, "opaque", entry.tint, entry.paint)?.model.set(seated);
        // The head turns a little into the corner — or, being sick, right
        // round to the side, leaning out and down over it — about the neck;
        // a loser's hangs.
        headPose(local, entry, steer, barfPose(kart.barf), look, nod + (done && !podium ? 0.25 : 0));
        let d = push(head, fade, "opaque", entry.tint, entry.paint);
        if (d) multiply(seated, local, d.model);
        const expression = part(entry.head, face);
        d = expression ? push(expression, fade, "opaque", entry.tint, entry.paint) : null;
        if (d) multiply(seated, local, d.model);
        if (kart.spin > 0 && star) {
          // Seeing stars: four of them circling over the head, each twirling.
          for (let i = 0; i < 4; i++) {
            const a = time * 7 + (i * Math.PI) / 2;
            transform(point, seated, entry.hx + Math.cos(a) * 0.8, entry.hy + 0.8 + Math.sin(a * 2) * 0.08, Math.sin(a) * 0.8);
            const s = push(star, 0, "opaque", WHITE4, PLAIN);
            if (s) place(s.model, point[0], point[1], point[2], time * 9 + i, 0, 0, 1.1);
          }
        }
      } else {
        // Before the models are in: the stand-in kart and a plain head.
        push(entry.box, fade, "opaque", entry.tint)?.model.set(placed);
        plainHead(-0.35, 1.75, 1.3);
      }

      if (air <= 0 && kart.speed > 3 && (kart.soak > 0 || inWater(race.track, kx, kz))) {
        // Wading through: a crown of spray churning up off each back wheel —
        // a faint wide one and a bright one inside it, surging out of step —
        // and droplets arcing off behind.
        const size = Math.min(1.8, kart.speed / 11);
        for (const side of SIDES) {
          for (let layer = 0; layer < 2; layer++) {
            const surge = 0.85 + 0.25 * Math.sin(time * 21 + layer * Math.PI + side + kart.id);
            const grow = size * (layer ? 0.65 : 1);
            const d = push(gpu.splash, 0, "alpha", sprayTints[layer]);
            if (d) multiply(placed, place(local, -0.95, 0, side * 0.85, time * 3 * side, 0, 0, grow, grow * surge, grow), d.model);
          }
          for (let i = 0; i < 5; i++) {
            const life = (time * 2.4 + i / 5 + kart.id * 0.31 + (side + 1) * 0.17) % 1;
            const fade = 1 - life;
            transform(point, placed, -0.95 - life * 2.4 * size, (1.8 * life - 1.9 * life * life) * 1.4 * size, side * (0.85 + life * 0.9));
            addEmber(point[0], point[1], point[2], 0.18 + 0.2 * fade, 0.5 * fade, 0.62 * fade, 0.72 * fade);
          }
        }
      }

      if (kart.boost > 0 && kart.moose <= 0 && !cart) {
        // Fire out of every exhaust: a flame that surges and flickers, a
        // white-hot core inside it, and embers streaming off behind.
        const surge = 0.8 + 0.4 * Math.min(1, kart.boost) + 0.25 * Math.sin(time * 37 + kart.id);
        for (const { at, angle } of EXHAUSTS[modelled ? entry.style.kind : "box"]) {
          const flare = 1 + 0.15 * Math.sin(time * 61 + at[2] * 9);
          // The flame, and its white-hot core.
          for (let core = 0; core < 2; core++) {
            const len = (core ? 1.3 : 2.3) * surge;
            const w = core ? 0.8 : 1.6 * flare;
            const d = push(gpu.flame, 5, "add", flameTint);
            if (d) multiply(placed, place(local, at[0], at[1], at[2], 0, angle, 0, len, w, w), d.model);
          }
          multiply(placed, place(local, at[0], at[1], at[2], 0, angle, 0), base);
          for (let i = 0; i < 6; i++) {
            const life = (time * 2.6 + i / 6 + kart.id * 0.37) % 1;
            transform(point, base, 0.5 + life * 2.6, 0.2 + life * 0.5, Math.sin(i * 12.9 + kart.id) * 0.25);
            // Yellow when fresh, cooling to a dim red as it drifts off.
            const fade = 1 - life;
            addEmber(point[0], point[1], point[2], 0.15 + 0.4 * fade, fade, fade * (0.2 + 0.6 * fade), fade * 0.1 * fade);
          }
          transform(point, base, 0.25, 0, 0);
          addEmber(point[0], point[1], point[2], 1.3 * surge, 0.6, 0.3, 0.06);
        }
      }
      if (kart.slip > 0.25) {
        // In someone's slipstream: air streaming past either side.
        for (const side of SIDES) {
          for (let i = 0; i < 4; i++) {
            const life = (time * 3 + i / 4 + side * 0.4) % 1;
            transform(point, placed, 2.2 - life * 5, 0.8 + i * 0.25, side * (1.3 + life * 0.3));
            addEmber(point[0], point[1], point[2], 0.22, 0.45 * (1 - life), 0.5 * (1 - life), 0.55 * (1 - life));
          }
        }
      }
      if (kart.drift && air <= 0 && kart.moose <= 0) {
        // Off the back wheels: dust while it charges, then sparks in the colour of its tier, thrown out of the corner.
        const tier = driftTier(race, kart);
        const [sr, sg, sb] = SPARKS[tier];
        if (tier > entry.tier) entry.flash = 0.2;
        entry.tier = tier;
        if (entry.flash > 0 && LOOK === "new") {
          // Up a tier: a flash of its colour off each back wheel.
          const f = entry.flash / 0.2;
          for (const side of SIDES) {
            transform(point, placed, -0.95, 0.35, side * 0.85);
            addEmber(point[0], point[1], point[2], 2.6 * f + 0.4, sr * f + 0.3 * f, sg * f + 0.3 * f, sb * f + 0.3 * f);
          }
        }
        const count = 4 + tier * 3;
        for (const side of SIDES) {
          for (let i = 0; i < count; i++) {
            const life = (time * 4 + i / count + side * 0.3 + kart.id * 0.17) % 1;
            const fade = 1 - life;
            transform(point, placed, -0.95 - life * 1.3, 0.15 + life * (0.9 - life * 0.8), side * 0.85 - kart.drift * life * 0.9);
            if (tier) addEmber(point[0], point[1], point[2], (0.3 + 0.14 * tier) * (0.5 + fade), sr * (0.5 + fade), sg * (0.5 + fade), sb * (0.5 + fade));
            else addPuff(point[0], point[1], point[2], 0.3 + life * 0.6, 0.8, 0.74, 0.62);
          }
        }
      }
      if (!kart.drift) entry.tier = 0;
      entry.flash = Math.max(0, entry.flash - dt);
      // Spun out: a burst of stars flying out from the driver.
      if (kart.spin > 0 && !entry.spun) entry.burst = 0.5;
      entry.spun = kart.spin > 0;
      entry.burst = Math.max(0, entry.burst - dt);
      if (entry.burst > 0 && LOOK === "new") {
        const t = 1 - entry.burst / 0.5;
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          addEmber(kx + Math.cos(a) * t * 3.2, lift + 1.6 + Math.sin(i * 2.1) * t * 1.2, kz + Math.sin(a) * t * 3.2, 0.7 * (1 - t) + 0.15, 1 - t * 0.5, 0.82 * (1 - t * 0.5), 0.23 * (1 - t));
        }
      }
      if (kart.player && cheering && LOOK === "new") {
        // Over the line: confetti raining down round you.
        for (let i = 0; i < 36; i++) {
          const fall = (time * 0.6 + i * 0.137) % 1;
          const c = DISCO[i % DISCO.length];
          const a = i * 2.39996;
          const r = 1.5 + (i % 5) * 0.9;
          addEmber(kx + Math.cos(a + time) * r, lift + 7 - fall * 7, kz + Math.sin(a + time) * r, 0.32, c[0], c[1], c[2]);
        }
      }
      if (kart.smoke > 0 && kart.moose <= 0) {
        // The smoke machine on the back, blowing out behind, and the cloud
        // it keeps round the kart: puffs rolling out to the edge of it,
        // growing as they go.
        const d = fogger && push(fogger, 0, "opaque", WHITE4, PLAIN);
        if (d) multiply(placed, place(local, -1.05, 1.2, 0, Math.PI, 0, 0, 1.3), d.model);
        const fade = Math.min(1, kart.smoke * 1.5);
        // The jet out of the nozzle: small puffs shooting back, swelling
        // and curling up as they slow.
        for (let i = 0; i < 14; i++) {
          const life = (time * 1.8 + i / 14 + kart.id * 0.21) % 1;
          const curl = Math.sin(i * 2.7 + time * 3) * life;
          // Up and out to the sides, not back into the camera behind.
          transform(point, placed, -1.36 - life * 1.6, 1.41 + life * 2.2, curl * 2.2);
          addPuff(point[0], point[1], point[2], (0.35 + life * 1.3) * fade, 0.97 - life * 0.08, 0.95 - life * 0.1, 0.9 - life * 0.12);
        }
        // The cloud it keeps round the kart: puffs rolling out low along the
        // road to its edge, turning slowly, swelling and thinning as they go.
        for (let i = 0; i < 22; i++) {
          const life = (time * 0.45 + i / 22) % 1;
          const angle = i * 2.39996 + kart.id + time * 0.35;
          const reach = 1.8 + life * (SMOKE_RADIUS - 2.5);
          const size = (1.4 + life * 2.8) * fade * Math.min(1, (1 - life) * 5, life * 8);
          addPuff(kx + Math.cos(angle) * reach, ground + 0.5 + life * 0.9 + Math.sin(time * 2 + i) * 0.15,
            kz + Math.sin(angle) * reach, size, 0.86 + 0.12 * ((i * 7) % 5) / 4, 0.84 + 0.1 * ((i * 7) % 5) / 4, 0.8 + 0.1 * ((i * 7) % 5) / 4);
        }
      }
      if (kart.barf > 0 && barfPose(kart.barf) > 0.6) {
        // Out of the mouth, over the side and down onto the road: lumps of
        // brown and green, arcing out and falling.
        if (part(entry.head, "head")) {
          multiply(placed, headPose(local, entry, steer, barfPose(kart.barf), look, nod), base);
          transform(mouth, base, entry.hx + 0.42, entry.hy - 0.12, 0);
          transform(point, base, entry.hx, entry.hy - 0.12, 0);
        } else {
          transform(mouth, placed, entry.hx, entry.hy - 0.1, 0.5);
          transform(point, placed, entry.hx, entry.hy - 0.1, 0);
        }
        const [ox, oy, oz] = [mouth[0], mouth[1], mouth[2]];
        const [dx, dz] = [ox - point[0], oz - point[2]];
        for (let i = 0; i < 16; i++) {
          const t = ((time * 2.2 + i / 16) % 1) * 0.55;
          const y = Math.max(ground + 0.05, oy + 1.2 * t - 13 * t * t);
          const tone = (i * 5) % 3;
          billboard(gpu.goo, ox + dx * 5 * t, y, oz + dz * 5 * t, 0.5 + t * 0.9, 0.5 + 0.12 * tone, 0.58 + 0.08 * tone, 0.16, 0, 0, 1, 1);
        }
      }
      if (cart || (kart.frenzy > 0 && kart.roll <= 0)) {
        // Rainbow sparks swirling round the trolley, as round a star.
        for (let i = 0; i < 10; i++) {
          const a = time * 5 + (i / 10) * Math.PI * 2;
          const hue = time * 4 + i * 0.6;
          addEmber(kx + Math.cos(a) * 1.6, ground + 0.6 + 0.5 * Math.sin(time * 7 + i), kz + Math.sin(a) * 1.6, 0.7,
            0.6 + 0.4 * Math.cos(hue), 0.6 + 0.4 * Math.cos(hue - 2.1), 0.6 + 0.4 * Math.cos(hue + 2.1));
        }
      }
      if (kart.bass > 0 && kart.moose <= 0) {
        // The Soundboks on the back, facing the karts behind, thumping on
        // every beat: a ring of bass rolling out over the road to the edge
        // of its reach, and notes rising off it.
        const since = Math.ceil(kart.bass / BASS_BEAT) * BASS_BEAT - kart.bass + (alpha - 1) * STEP;
        const pulse = Math.exp(-Math.max(0, since) * 12);
        const d = speaker && push(speaker, 0, "opaque", WHITE4, PLAIN);
        if (d) multiply(placed, place(local, -1.1, 1.2, 0, Math.PI, 0, 0, 1.1 + pulse * 0.18), d.model);
        const wave = Math.max(0, since) / BASS_BEAT;
        const ring = BASS_RADIUS * Math.sqrt(wave);
        for (let i = 0; i < 48; i++) {
          const a = (i / 48) * Math.PI * 2;
          const glow = 1 - wave;
          addEmber(kx + Math.cos(a) * ring, ground + 0.4, kz + Math.sin(a) * ring, 1.2 + glow, glow, glow * 0.35, glow * 0.9);
        }
        transform(point, placed, -1.1, 2.2, 0);
        // The sound itself, rising off it in pink and blue sparks.
        for (let i = 0; i < 6; i++) {
          const life = (time * 0.9 + i / 6) % 1;
          const sway = Math.sin(time * 4 + i * 2) * 0.6;
          const fade = Math.min(1, (1 - life) * 3);
          addEmber(point[0] + right[0] * sway, point[1] + life * 2.4, point[2] + right[2] * sway, 0.3 * fade + 0.05, i % 2 ? 1 : 0.3, i % 2 ? 0.3 : 0.8, 1);
        }
      }
      if (kart.itch > 0) {
        // The swarm that bit you, still buzzing round your head.
        const sy = lift + entry.hy + 0.4 + Math.sin(time * 11) * 0.1;
        if (swarm) place(push(swarm, 0, "opaque", WHITE4, PLAIN)?.model ?? local, kx + hx * entry.hx, sy, kz + hz * entry.hx, time * 7, 0, 0, 0.8);
      }
      if (kart.slide > 0 && kart.speed > 3 && air <= 0) {
        // Blue slush sprayed up off the back wheels.
        for (let i = 0; i < 8; i++) {
          const life = (time * 3 + i / 8 + kart.id * 0.3) % 1;
          const fade = 1 - life;
          transform(point, placed, -0.95 - life * 1.8, 0.2 + life * (1.2 - life), i % 2 ? 0.9 : -0.9);
          addEmber(point[0], point[1], point[2], 0.3 + 0.4 * fade, 0.25 * fade, 0.55 * fade, fade);
        }
      }
    }

    scenery(race, time);

    if (ghost && ghost.length >= 8 && !race.autopilot) {
      // The ghost: where the recorded run was at this moment of its race, a
      // see-through kart and driver — or, before their models are in, a sprite.
      const at = Math.max(0, time - COUNTDOWN) / GHOST_STEP;
      const i = Math.min(ghost.length / 4 - 2, Math.floor(at));
      const f = Math.min(1, at - i);
      const g = (k: number) => lerp(ghost![i * 4 + k], ghost![i * 4 + 4 + k], f);
      const heading = lerpAngle(ghost[i * 4 + 3], ghost[i * 4 + 7], f);
      const [gx, gy, gz] = [g(0), g(1), g(2)];
      place(placed, gx, gy, gz, -heading);
      const mine = scene.karts.get(player.id)!;
      const body = models.get(mine.kartModel);
      const torso = part(mine.body, "torso");
      if (body && torso) {
        push(body, 0, "alpha", ghostTint, PLAIN)?.model.set(placed);
        push(torso, 0, "alpha", ghostTint, PLAIN)?.model.set(placed);
      }
    }

    for (const shot of race.shots) {
      const sx = lerp(shot.px, shot.x, alpha);
      const sz = lerp(shot.pz, shot.z, alpha);
      const sy = roadY(track, shot.idx, sx, sz);
      if (!isPuddle(shot)) addShadow(sx, sy, sz, 1.1);
      if (shot.kind === "rocket") {
        // High over the karts, trailing sparks.
        const v = Math.hypot(shot.vx, shot.vz) || 1;
        const rocket = push(gpu.rocket, 0, "opaque", WHITE4, PLAIN);
        if (rocket) place(rocket.model, sx, sy + 2.6, sz, -Math.atan2(shot.vz, shot.vx), -Math.PI / 2, Math.sin(time * 20) * 0.3, 1.3);
        for (let i = 0; i < 10; i++) {
          const life = (time * 5 + i / 10) % 1;
          const back = 0.8 + life * 3;
          addEmber(sx - (shot.vx / v) * back, sy + 2.6 - life * 0.4, sz - (shot.vz / v) * back, 0.5 * (1 - life) + 0.2, 1, 0.6 * (1 - life), 0.15);
        }
        continue;
      }
      const mesh = models.get(itemModel(shot.kind));
      const d = mesh && push(mesh, 0, "opaque", WHITE4, PLAIN);
      if (!d) continue;
      // A puddle lies where it fell, at whatever angle; a glove flies fist
      // first; a can flies like a rocket; a swarm swirls and jitters as it hunts.
      if (isPuddle(shot)) place(d.model, sx, sy, sz, shot.x * 7.3 + shot.z * 3.1, 0, 0, 1.9);
      else if (shot.kind === "glove") place(d.model, sx, sy + 0.85, sz, -Math.atan2(shot.vz, shot.vx), 0, 0, 1.7);
      else if (shot.kind === "can") {
        // Nose first, like a rocket, beer foam spraying out of its tail.
        const v = Math.hypot(shot.vx, shot.vz) || 1;
        const [bx, bz] = [-shot.vx / v, -shot.vz / v];
        place(d.model, sx, sy + 0.9, sz, -Math.atan2(shot.vz, shot.vx), -Math.PI / 2, 0, 2.1);
        for (let i = 0; i < 6; i++) {
          const life = (time * 6 + i / 6) % 1;
          const back = 0.6 + life * 2.2;
          const wobble = Math.sin(i * 2.3 + time * 20) * life * 0.25;
          addPuff(sx + bx * back - bz * wobble, sy + 0.9 + wobble, sz + bz * back + bx * wobble, 0.25 + life * 0.5, 1, 0.97, 0.85);
        }
      }
      else {
        const y = sy + 1.1 + Math.sin(time * 7) * 0.15;
        place(d.model, sx + Math.sin(time * 23) * 0.08, y, sz + Math.cos(time * 19) * 0.08, time * 6, 0, 0, 2.2);
      }
    }

    const glass = models.get("item-box");
    const mark = models.get("item-mark");
    let boxes = 0;
    for (const box of race.boxes) {
      if (box.respawn > 0) continue;
      const y = box.y + 1.3 + Math.sin(time * 3 + box.x) * 0.15;
      addShadow(box.x, box.y, box.z, 1);
      place((boxSpin[boxes++] ??= new Float32Array(16)), box.x, y, box.z, time * 1.5 + box.z);
      // The "?" turns to face the camera, rocking a little, so it always
      // reads as one — spinning with the box it spends half its time edge-on.
      const facing = -(camHeading + Math.PI) + Math.sin(time * 2.5 + box.x) * 0.35;
      const d = mark && push(mark, 0, "opaque", WHITE4, PLAIN);
      if (d) place(d.model, box.x, y, box.z, facing, 0, 0, 1.25);
      // Glints winking on and off round it.
      for (let k = 0; k < 3; k++) {
        const glint = Math.max(0, Math.sin(time * 3.7 + k * 2.1 + box.x));
        const a = time * 1.3 + k * 2.09 + box.z;
        if (glint > 0.2) addEmber(box.x + Math.cos(a) * 1.1, y + Math.sin(time * 2 + k) * 0.8, box.z + Math.sin(a) * 1.1, 0.55 * glint, glint, glint, glint * 0.85);
      }
    }

    backend.updateDynamic(gpu.shadows);
    backend.updateDynamic(gpu.embers);
    backend.updateDynamic(gpu.smoke);
    backend.updateDynamic(gpu.goo);
    // See-through last: the lakes, shadows, then the item boxes around their "?"…
    push(scene.water, 0, "alpha", waterTint)?.model.set(IDENTITY);
    push(gpu.shadows, 4, "alpha", shadowTint)?.model.set(IDENTITY);
    push(gpu.smoke, 4, "alpha", smokeTint)?.model.set(IDENTITY);
    push(gpu.goo, 4, "alpha", WHITE4)?.model.set(IDENTITY);
    for (let i = 0; i < boxes; i++) {
      if (!glass) {
        push(gpu.cube, 0, "alpha", cubeTint)?.model.set(boxSpin[i]);
        continue;
      }
      // Glass that shifts through the rainbow, each box a little out of step.
      const tint = (glassTints[i] ??= new Float32Array(4));
      const hue = (time * 0.6 + i * 0.13) * Math.PI * 2;
      tint[0] = 0.65 + 0.35 * Math.cos(hue);
      tint[1] = 0.65 + 0.35 * Math.cos(hue - 2.1);
      tint[2] = 0.65 + 0.35 * Math.cos(hue + 2.1);
      tint[3] = 0.38;
      push(glass, 0, "alpha", tint, PLAIN)?.model.set(boxSpin[i]);
    }
    // …and fire, added on top: it lights what's behind it rather than
    // covering it.
    push(gpu.embers, 6, "add")?.model.set(IDENTITY);
    // Warm-up: each new model drawn once, shrunk to a point — its buffers
    // reach the GPU now rather than in the frame it first comes into view.
    while (fresh.length) {
      const d = push(fresh.pop()!, 0, "opaque", WHITE4, PLAIN);
      if (d) place(d.model, ex, ey, ez, 0, 0, 0, 0);
    }
    // The sky last of the solid things, so all it covers is what nothing else has.
    if (scene.backdrop && scene.clouds) {
      const d = push(scene.backdrop, 8, "opaque");
      if (d) place(d.model, ex, camY, ez);
      const c = push(scene.clouds, 3, "opaque");
      if (c) place(c.model, ex, camY, ez);
    }

    // Cull what's placed (karts, items, creatures, the disco) outside the
    // view across, behind the camera, or past the fog; the world's meshes and
    // the particle buffers are drawn whole.
    const tanH = Math.tan(fov / 2) * aspect * 1.1;
    const vl = Math.hypot(dx, dz);
    const vx = dx / vl;
    const vz = dz / vl;
    let kept = 0;
    for (let i = 0; i < frame.count; i++) {
      const d = frame.draws[i];
      const m = d.model;
      if (!whole.has(d.mesh)) {
        const r = 4 * Math.hypot(m[0], m[1], m[2]) + 2;
        const ox = m[12] - ex;
        const oz = m[14] - ez;
        const along = ox * vx + oz * vz;
        if (along < -r || along > 320 + r || Math.abs(oz * vx - ox * vz) > along * tanH + r * 1.5) continue;
      }
      frame.draws[i] = frame.draws[kept];
      frame.draws[kept++] = d;
    }
    frame.count = kept;
    stats.draws = frame.count;
    stats.particles = gpu.embers.quads + gpu.smoke.quads + gpu.goo.quads;
    stats.triangles = 0;
    for (let i = 0; i < frame.count; i++) stats.triangles += frame.draws[i].mesh.count / 3;
    backend.render(frame);
  };
  const stats = { draws: 0, triangles: 0, viewProj: frame.viewProj, particles: 0, overdraw: 0 };
  /** A billboard's share of the screen is its size² times this, over its distance²; set with the camera. */
  let cover = 0;
  const glassTints: Float32Array[] = [];
  /** Meshes drawn whole, never culled: the world's, and the particle buffers. */
  const whole = new Set<Mesh>();

  return {
    get kind() {
      return backend.kind;
    },
    setRace,
    stats,
    setGhost(path) {
      ghost = path;
    },
    kick(amount) {
      shake = Math.max(shake, amount);
    },
    resize(w, h, dpr) {
      width = w;
      height = h;
      pixels = [Math.round(w * dpr), Math.round(h * dpr)];
      backend.resize(pixels[0], pixels[1]);
    },
    render,
    destroy() {
      destroyed = true;
      scene?.freeAll();
      models.forEach((m) => backend.free(m));
      gpu.freeAll();
      backend.destroy();
      scene = null;
    },
  };
}

/** The meshes every race shares, on whichever backend. */
function makeResources(backend: Backend) {
  const upload = (b: MeshBuilder) => backend.mesh(new Float32Array(b.v), new Uint16Array(b.i), false);
  const r = {
    cube: upload(boxMesh()),
    flame: upload(flameMesh()),
    ...propMeshes(upload),
    branch: upload(branchMesh()),
    duck: upload(duckMesh(false)),
    chick: upload(duckMesh(true)),
    duckWing: upload(wingMesh(false)),
    chickWing: upload(wingMesh(true)),
    splash: upload(splashMesh()),
    snowman: upload(snowmanMesh()),
    skier: upload(skierMesh([0.9, 0.2, 0.2])),
    skier2: upload(skierMesh([0.15, 0.55, 0.9])),
    shadows: backend.dynamicMesh(256),
    embers: backend.dynamicMesh(2048),
    smoke: backend.dynamicMesh(256),
    /** Sick, in flight: as the smoke, but thick enough not to see through. */
    goo: backend.dynamicMesh(128),
    freeAll() {
      [r.cube, r.flame, r.branch, r.duck, r.chick, r.duckWing, r.chickWing, r.splash, r.snowman, r.skier, r.skier2, r.beam, r.tile, r.disco, ...r.balls, r.snowball, r.mallet, r.kubb, r.king, r.cow, r.mosquito, r.dj, ...r.dancers, r.rocket, r.arrow, r.headSkin, r.headHair, r.shadows, r.embers, r.smoke, r.goo].forEach((m) => backend.free(m));
    },
  };
  return r;
}

/** A cow, facing +x, hooves on y = 0: white, black-patched, with a bell. */
function cowMesh() {
  const b = new MeshBuilder();
  const white: Rgb = [0.96, 0.95, 0.92];
  const black: Rgb = [0.12, 0.11, 0.12];
  const pink: Rgb = [0.95, 0.7, 0.7];
  b.box(0, 1.0, 0, 2.0, 0.9, 0.85, white);
  b.box(-0.35, 1.46, 0.02, 0.7, 0.04, 0.55, black);
  b.box(0.45, 1.46, -0.1, 0.4, 0.04, 0.4, black);
  for (const side of SIDES) {
    b.box(0.35, 1.05, side * 0.43, 0.6, 0.4, 0.03, black);
    b.box(-0.6, 0.9, side * 0.43, 0.45, 0.35, 0.03, black);
    for (const x of [-0.7, 0.7]) {
      b.box(x, 0.36, side * 0.28, 0.24, 0.72, 0.24, white);
      b.box(x, 0.04, side * 0.28, 0.27, 0.1, 0.27, black);
    }
    b.box(1.22, 1.55, side * 0.3, 0.1, 0.06, 0.2, black);
    b.box(1.24, 1.66, side * 0.15, 0.07, 0.2, 0.07, [0.9, 0.85, 0.7]);
    b.box(1.5, 1.35, side * 0.15, 0.04, 0.08, 0.08, black);
  }
  b.box(1.25, 1.25, 0, 0.5, 0.5, 0.5, white);
  b.box(1.55, 1.1, 0, 0.22, 0.3, 0.42, pink);
  b.box(1.05, 0.95, 0, 0.12, 0.14, 0.14, [0.9, 0.7, 0.2]);
  b.box(-0.3, 0.55, 0, 0.45, 0.14, 0.35, pink);
  b.box(-1.05, 1.05, 0, 0.06, 0.7, 0.06, white);
  b.box(-1.05, 0.68, 0, 0.12, 0.16, 0.12, black);
  return b;
}

/** A mosquito, facing +x, about a metre long: thin grey body, long proboscis, pale wings, six legs. */
function mosquitoMesh() {
  const b = new MeshBuilder();
  const body: Rgb = [0.3, 0.28, 0.27];
  b.box(-0.32, 0, 0, 0.5, 0.09, 0.09, body);
  for (const x of [-0.4, -0.3, -0.2]) b.box(x, 0.005, 0, 0.04, 0.1, 0.1, [0.85, 0.83, 0.78]);
  b.sphere(0, 0, 0, 0.11, body, 4, 6);
  b.sphere(0.14, -0.01, 0, 0.06, [0.15, 0.13, 0.13], 4, 6);
  b.box(0.32, -0.05, 0, 0.26, 0.015, 0.015, [0.15, 0.13, 0.13]);
  for (const side of SIDES) {
    b.box(-0.1, 0.09, side * 0.22, 0.38, 0.01, 0.22, [0.82, 0.88, 0.95]);
    for (const x of [-0.06, 0.02, 0.1]) b.box(x, -0.09, side * 0.11, 0.015, 0.16, 0.015, [0.15, 0.13, 0.13]);
  }
  return b;
}

/** Who's at the disco: shirt, trousers, hair, skin; the DJ in headphones, arms forward to the decks. */
const DANCERS = [
  { shirt: [0.95, 0.3, 0.55], pants: [0.15, 0.15, 0.25], hair: [0.35, 0.2, 0.1], skin: [0.95, 0.76, 0.62] },
  { shirt: [0.2, 0.75, 0.9], pants: [0.2, 0.2, 0.22], hair: [0.1, 0.08, 0.08], skin: [0.6, 0.42, 0.3] },
  { shirt: [0.98, 0.8, 0.2], pants: [0.3, 0.3, 0.6], hair: [0.85, 0.7, 0.35], skin: [0.98, 0.82, 0.7] },
  { shirt: [0.4, 0.85, 0.4], pants: [0.6, 0.15, 0.2], hair: [0.5, 0.15, 0.1], skin: [0.8, 0.6, 0.45] },
] as { shirt: Rgb; pants: Rgb; hair: Rgb; skin: Rgb; dj?: boolean }[];

/** A person facing +x, feet on y = 0, about 1.8 tall: dancers with their arms in the air. */
function personMesh({ shirt, pants, hair, skin, dj }: { shirt: Rgb; pants: Rgb; hair: Rgb; skin: Rgb; dj?: boolean }) {
  const b = new MeshBuilder();
  for (const side of SIDES) {
    b.box(0, 0.42, side * 0.12, 0.2, 0.85, 0.2, pants);
    b.box(0.06, 0.04, side * 0.12, 0.3, 0.08, 0.2, [0.12, 0.12, 0.14]);
    if (dj) {
      // Down to the decks: upper arm, then forearm reaching out.
      b.box(0.05, 1.2, side * 0.33, 0.14, 0.45, 0.14, shirt);
      b.box(0.3, 1.0, side * 0.33, 0.45, 0.12, 0.12, skin);
    } else {
      b.box(0, 1.55, side * 0.34, 0.13, 0.7, 0.13, shirt);
      b.box(0, 1.95, side * 0.34, 0.12, 0.14, 0.12, skin);
    }
  }
  b.box(0, 1.15, 0, 0.3, 0.6, 0.5, shirt);
  b.box(0, 1.5, 0, 0.14, 0.1, 0.14, skin);
  b.sphere(0, 1.72, 0, 0.19, skin, 4, 8);
  b.sphere(-0.03, 1.79, 0, 0.2, hair, 4, 8);
  for (const side of SIDES) b.box(0.17, 1.74, side * 0.07, 0.04, 0.04, 0.04, [0.1, 0.1, 0.1]);
  if (dj) {
    // Headphones: a band over the head, a cup over each ear.
    b.box(0, 1.93, 0, 0.06, 0.05, 0.44, [0.1, 0.1, 0.12]);
    for (const side of SIDES) b.box(0, 1.75, side * 0.22, 0.14, 0.16, 0.07, [0.1, 0.1, 0.12]);
  }
  return b;
}

/** A New Year's rocket, nose along +y, centred on its middle: a red body, a white band, an orange cone, fins, a wooden stick. */
function rocketMesh() {
  const b = new MeshBuilder();
  b.cylinder(0, -0.55, 0, 0.03, 1.4, [0.7, 0.55, 0.35], "y", 5);
  b.cylinder(0, 0.1, 0, 0.17, 0.9, [0.86, 0.14, 0.14], "y", 10);
  b.cylinder(0, 0.1, 0, 0.175, 0.14, [0.97, 0.97, 0.97], "y", 10);
  b.cone(0, 0.55, 0, 0.17, 0.5, [0.98, 0.5, 0.1], 10);
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) b.box(dx * 0.2, -0.28, dz * 0.2, dz ? 0.02 : 0.14, 0.3, dx ? 0.02 : 0.14, [0.98, 0.8, 0.15]);
  return b;
}

/** A marker, tip up, on a short shaft; drawn flipped over a head. */
function arrowMesh() {
  const b = new MeshBuilder();
  b.cylinder(0, 0.15, 0, 0.11, 0.5, [0.9, 0.15, 0.12], "y", 8);
  b.cone(0, 0.4, 0, 0.36, 0.55, [0.9, 0.15, 0.12], 8);
  b.cone(0, 0.4, 0, 0.36, 0.02, [1, 1, 1], 8);
  return b;
}

/** A head, radius 1, nose along +x: white to be tinted skin, with dark eyes — or, `hair`, the hair over the back and top of it. */
function headMesh(hair: boolean) {
  const b = new MeshBuilder();
  if (hair) {
    b.sphere(-0.16, 0.2, 0, 1.06, [1, 1, 1], 5, 10);
    return b;
  }
  b.sphere(0, 0, 0, 1, [1, 1, 1], 6, 10);
  for (const side of SIDES) b.box(0.93, 0.15, side * 0.32, 0.14, 0.16, 0.14, [0.06, 0.06, 0.06]);
  b.box(1.0, -0.1, 0, 0.16, 0.2, 0.14, [0.92, 0.92, 0.92]);
  return b;
}

/** The moving parts of the scenery, placed each frame. */
function propMeshes(upload: (b: MeshBuilder) => Mesh) {
  // A light's beam along +x, 0…1: two crossed strips, so it reads from any side.
  const beam = new MeshBuilder();
  beam.quad([0, -0.5, 0], [0, 0.5, 0], [1, 0.5, 0], [1, -0.5, 0], [0, 0, 1], [1, 1, 1], [[0, 0], [1, 0], [1, 1], [0, 1]]);
  beam.quad([0, 0, -0.5], [0, 0, 0.5], [1, 0, 0.5], [1, 0, -0.5], [0, 1, 0], [1, 1, 1], [[0, 0], [1, 0], [1, 1], [0, 1]]);
  const tile = new MeshBuilder();
  tile.quad([-0.5, 0, -0.5], [0.5, 0, -0.5], [0.5, 0, 0.5], [-0.5, 0, 0.5], [0, 1, 0], [1, 1, 1]);
  // Mirror tiles in not quite the same grey, so it glitters as it turns.
  const disco = new MeshBuilder();
  disco.sphere(0, 0, 0, 1, (i, k) => [[0.95, 0.95, 1], [0.7, 0.72, 0.78], [0.85, 0.85, 0.9]][(i + k * 2) % 3] as Rgb, 8, 14);
  // Croquet balls in each of the game's colours, a white band round them to see them roll.
  const balls = CROQUET.map((col) => {
    const ball = new MeshBuilder();
    ball.sphere(0, 0, 0, BALL_RADIUS, (_, k) => (k % 8 === 4 ? [0.97, 0.97, 0.97] : col), 8, 16);
    return upload(ball);
  });
  // A snowball, packed not quite round, grey-blue in its dents.
  const snowball = new MeshBuilder();
  snowball.sphere(0, 0, 0, BALL_RADIUS, (i, k) => ((i * 3 + k * 5) % 7 ? [0.97, 0.98, 1] : [0.8, 0.86, 0.95]), 8, 16);
  // A mallet hung from the top of its handle, head along +x, to swing.
  const mallet = new MeshBuilder();
  const wood: Rgb = [0.78, 0.6, 0.38];
  mallet.cylinder(0, -1.8, 0, 0.14, 3.6, wood, "y", 8);
  mallet.log(-1.1, 0, 1.1, 0, 0.55, wood, CROQUET[0], -3.6, 10);
  mallet.log(-0.9, 0, -0.75, 0, 0.57, CROQUET[3], CROQUET[3], -3.6, 10);
  mallet.log(0.75, 0, 0.9, 0, 0.57, CROQUET[3], CROQUET[3], -3.6, 10);
  // Kubb, giant size: a block, and the king with his crown, their origin at the foot.
  const kubbWood: Rgb = [0.86, 0.73, 0.5];
  const kubb = new MeshBuilder();
  kubb.box(0, 0.9, 0, 0.75, 1.8, 0.75, kubbWood);
  const king = new MeshBuilder();
  king.box(0, 1.3, 0, 1.1, 2.6, 1.1, kubbWood);
  king.box(0, 2.65, 0, 1.2, 0.1, 1.2, [0.9, 0.75, 0.2]);
  for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) king.box(dx * 0.38, 2.95, dz * 0.38, 0.28, 0.5, 0.28, [0.95, 0.8, 0.25]);
  return {
    beam: upload(beam), tile: upload(tile), disco: upload(disco), balls, snowball: upload(snowball), mallet: upload(mallet), kubb: upload(kubb), king: upload(king),
    cow: upload(cowMesh()),
    mosquito: upload(mosquitoMesh()),
    dj: upload(personMesh({ shirt: [0.55, 0.15, 0.8], pants: [0.12, 0.12, 0.16], hair: [0.1, 0.08, 0.08], skin: [0.87, 0.68, 0.55], dj: true })),
    dancers: DANCERS.map((d) => upload(personMesh(d))),
    rocket: upload(rocketMesh()),
    arrow: upload(arrowMesh()),
    headSkin: upload(headMesh(false)),
    headHair: upload(headMesh(true)),
  };
}

function uploadModel(backend: Backend, { vertices, indices, data }: ModelData) {
  return backend.mesh(new Uint8Array(data, 0, vertices * PACKED_STRIDE), new Uint16Array(data, vertices * PACKED_STRIDE, indices), true);
}
